// Copenhagen, "Midsummer evening in Nyhavn". A white night in late June, seen from the south quay of Nyhavn (the quiet
// Charlottenborg side, a little east of the canal's head at Kongens Nytorv), looking north across the water at the
// sunny side: the painted townhouses No. 3 to No. 25 with their red tile roofs and dormers (a curved and a stepped gable
// among them), the dark red No. 3, the light blue No. 5, the low blue No. 9 from 1681 with its gabled wall dormer, the
// corner of Lille Strandstraede, restaurant terraces under parasols all along the quay and the wooden ships of the
// veteran harbour moored in front, their masts and rigging standing before the houses. The sun is just under the
// northern horizon: the sky stays a luminous blue with a peach band behind the roofs. The near quay is the foreground
// and it is full of people: friends sitting on the coping, a guitarist, a hot dog wagon, a child blowing soap bubbles,
// strollers, and a stream of cyclists and cargo bikes; the bow of one of the museum's wooden ships lies at our quay on the right. Canal tour boats, kayaks, a picnic boat and a swan family use the water; a
// ship hoists its tanbark mainsail, a jazz band glides by on a barge and a hot-air balloon drifts over the roofs.
// The city's billboard is a lit sign on a steel frame on the roof of No. 7.
// render(t, { crowd }) thins the people on both quays and aboard the boats; crowd 0 draws nobody at all (see CROWD).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, text, textWidth, glyph, reflect, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HB = 152;                                     // the house fronts meet the north quay
const FQ = 160;                                     // feet of the people strolling on the far quay
const FEDGE = 162;                                  // far quay coping
const WL = 170;                                     // far waterline
const NEDGE = 208;                                  // near (south) quay coping: people sit on it
const LANE_A = 224, LANE_BIKE = 243, LANE_B = 257;  // near quay: strollers, the cycle lane, the nearest strollers (feet rows)
const SH = 16, BAY = 8, RH = 17;                    // storey height, bay pitch, roof height
// the sunny side from west (left) to east: No. 3 .. No. 25; st storeys over a basement band of bsm px. Most fronts
// have their eaves to the quay under a red tile roof (rh px high, RH by default) with dormers; kvist is a gabled wall
// dormer over bays at .. at + n - 1 ('pitch': a pediment, 'curve': a Baroque curved gable); gable is a front that rises
// into a gable of its own ('step': stepped, 'curve': a curved neck gable), the old warehouses' hoist beam at the top
const HOUSES = [
  { no: 3, x0: -6, bays: 5, st: 3, bsm: 6, col: '#a8342c', dorm: 2, band: true, shop: 'KRO', fas: '#1c3a2e' },
  { no: 5, x0: 36, bays: 5, st: 4, bsm: 6, col: '#98bede', dorm: 3, band: true, shop: 'FISK', fas: '#16284e' },
  { no: 7, x0: 78, bays: 6, st: 4, bsm: 0, col: '#ecc858', dorm: 3, shop: 'VAFLER', fas: '#5a1a1e' },
  { no: 9, x0: 128, bays: 4, st: 2, bsm: 4, col: '#3a78d0', kvist: { at: 1, n: 2, top: 'pitch' }, shop: 'ANKER', fas: '#f0e8d8', ink: '#16284e' },
  { no: 11, x0: 162, bays: 5, st: 4, bsm: 3, col: '#f08a2c', dorm: 4, shop: 'KAFFE', fas: '#2a1a12' },
  { no: 13, x0: 204, bays: 4, st: 4, bsm: 0, col: '#c64a3a', gable: 'curve', shop: 'SILD', fas: '#16284e' },
  { no: 15, x0: 238, bays: 4, st: 3, bsm: 6, col: '#efe6d0', dorm: 1, rh: 14, shop: 'VIN', fas: '#5a1a1e' },
  { no: 17, x0: 272, bays: 6, st: 3, bsm: 6, col: '#e4b23c', dorm: 2, kvist: { at: 2, n: 2, top: 'curve' }, shop: 'BODEGA', fas: '#1c3a2e' },
  { no: 19, x0: 338, bays: 4, st: 5, bsm: 2, col: '#bccab6', dorm: 2, rh: 15, shop: 'IS', fas: '#c8425a', ink: '#fff0e0' },
  { no: 21, x0: 372, bays: 4, st: 4, bsm: 5, col: '#d6705c', gable: 'step', shop: 'KAJ', fas: '#2a1a12' },
  { no: 23, x0: 406, bays: 5, st: 3, bsm: 6, col: '#eed47c', dorm: 2, kvist: { at: 2, n: 1, top: 'pitch' }, shop: 'SKIPPER', fas: '#16284e' },
  { no: 25, x0: 448, bays: 4, st: 4, bsm: 0, col: '#6f95c2', dorm: 2, rh: 20, shop: 'HAVN', fas: '#1c3a2e' },
];
for (const h of HOUSES) { h.w = h.bays * BAY + 2; h.base = HB - h.bsm; h.eave = h.base - h.st * SH; h.ridge = h.eave - (h.rh || RH); }
const SIDE = { x0: 322, x1: 338 };                  // Lille Strandstraede, between No. 17 and No. 19
const HERO = HOUSES[3], SIGN_HOUSE = HOUSES[2];
const BB = { x: 83, y: 48, w: 41, h: 17 };          // face of the roof sign on No. 7 (the city's billboard)
const BB_LAMPS = [BB.x + 6, BB.x + 20, BB.x + 34], BB_LAMP_Y = BB.y - 5;
const FAR_LAMPS = [108, 218, 330, 420];             // lanterns on the far quay's edge
const NEAR_LAMPS = [{ x: 122, y: 160 }, { x: 410, y: 160 }];   // the two lanterns on our quay (x, lantern centre row)
const CART = { x: 10, y: 194, w: 44, h: 38 };       // the hot dog wagon on our quay (body box)
const BUBBLE_KID = { x: 342, y: 218 };              // feet of the child with the bubble wand
const GUITAR = { x: 196, y: 218 };                  // the guitarist's crate
// The share of the crowd the scene shows by default: 1 = everyone, 0.5 = about half, just above 0 = only the cast the
// scene's story needs (the hot dog vendor and his customer, the guitarist, the friends' photo, the bubble child, the
// crews, the band and everyone a timed event is about). Tune it per city; render(t, { crowd }) overrides it for one frame.
const CROWD = 0.5;
// Crowd 0 itself is the visitors' "None": nobody is drawn, the cast included, and what is theirs goes with them (the
// guitar and its notes, the soap bubbles, the kayaks, the cargo bike, the band and its instruments; the sail stays
// furled and nobody throws the swans bread). What people use stays, unattended and lit: the hot dog wagon and its
// steam, the terraces' candles and heaters, the parked bikes. Boats under engine still pass, with nobody aboard but
// the one who steers; the barge glides by under her bulbs, her deck empty.
const nobody = crowd => crowd === 0;
// Everyone who can thin has a fixed rank in (0, 1), given once at build time, and is drawn while rank < crowd. A unit
// (a pair, a parent and child, owner and dog, rider and bike, a terrace table) shares one rank. Golden-ratio steps
// over a group's index keep the thinning even along the group.
const rankOf = (i, off = 0) => frac((i + 0.5) * 0.6180339887 + off);

/* ================= Timed events (loop seconds) ================= */
const TOUR_OUT = { t0: 6, t1: 52 };                 // a canal tour boat heads out to the harbour (left to right)
const KAYAK = { t0: 58, t1: 108 };                  // two kayaks come in from the harbour
const PICNIC = { t0: 84, t1: 146 };                 // a little picnic boat putters east
const TOUR_IN = { t0: 126, t1: 172 };               // the tour boat comes home
const JAZZ = { pre: 168, t0: 182, t1: 234 };        // big moment: a jazz band on a barge (the quay turns to look from `pre`)
const SAIL = { pre: 102, up0: 116, up1: 130, dn0: 160, dn1: 168 };   // big moment: the ketch hoists her mainsail
const BALLOON = { t0: 20, t1: 150 };                // big moment: a hot-air balloon drifts over the roofs
const DOG_T = 40;                                   // a customer walks up to the hot dog wagon every 40 s
const PHOTO = [30, 90, 150, 210];                   // the friends by the edge get their picture taken (two flashes)
const TOURIST = { t0: 60, t1: 100, bell: 79 };      // a tourist stops at the cycle lane; a cargo bike rings
const FEED = { t0: 98, t1: 118, splash: 108 };      // the swans come over for bread
const TOASTS = [[8, 11], [20, 23], [75, 78], [140, 143], [195, 198]];   // friends on the coping clink bottles (one group each)
const GULL_HOP = { land: 44, off: 56 };             // a gull lands on a bollard until a child runs at it

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = C(a), B = C(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    text(s, i, j, col) { this.flush(); text(x, s, i, j, col); this.grab(); },
    flush() { x.putImageData(img, 0, 0); },
    grab() { d.set(x.getImageData(0, 0, w, h).data); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const box = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** piecewise-linear script: keys [[t, x, y], ...] sorted by t; returns the position at t, whether it moves and which way */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], y: keys[0][2], moving: false, dir: 1 };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i], u = (t - a[0]) / (b[0] - a[0]);
    return { x: lerp(a[1], b[1], u), y: lerp(a[2], b[2], u), moving: a[1] !== b[1] || a[2] !== b[2], dir: b[1] >= a[1] ? 1 : -1 };
  }
  const z = keys[keys.length - 1];
  return { x: z[1], y: z[2], moving: false, dir: 1 };
}
// the Danish letters the signs need, for text()'s 3x5 font (drawn locally with glyph())
// (the slashed O is five wide: its slash sticks out into the gaps on either side)
const DK = { 'Ø': ['..1.1', '.1.1.', '.111.', '.1.1.', '1.1..'] };
function sign(c, s, x, y, col) {
  let cx = x;
  for (const ch of s) { if (DK[ch]) glyph(c, DK[ch], cx - 1, y, col); else text(c, ch, cx, y, col); cx += 4; }
}

/* ================= The white-night sky: luminous blue above, a peach band behind the roofs (we face north) ================= */
const SKY = ['#18245a', '#1c2e6a', '#213a7a', '#284888', '#325894', '#406aa0', '#527eac', '#6c96b6', '#8cacba', '#b2c0b6',
  '#d6caa8', '#f0c896', '#f8b47e', '#f69a66'];
const SKY_H = 156;
function renderSky() {
  const p = pix(W, SKY_H);
  for (let y = 0; y < SKY_H; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(clamp(y / 100, 0, 1), 1.12);
    v += 0.07 * Math.exp(-(((x - 170) / 230) ** 2)) * clamp(y / 90, 0, 1);            // the sun is under the horizon, north-north-west
    v += (vnoise(x / 60, y / 9, 3) - 0.5) * 0.05;
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(23);
  while (STARS.length < 22) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 44), b = r();
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: (0.25 + b * b * 0.6) * (1 - y / 60) });
  }
}
/** a tileable band of thin, separate cloud streaks (period W): cool on top, lit from below by the sun under the horizon */
function makeWisps(seed, h, n, pal, alpha) {
  const r = rng(seed), S = [];
  for (let i = 0; i < n; i++) S.push({ x: r() * W, y: h * (0.25 + r() * 0.5), len: 16 + r() * 36, th: 1.4 + r() * 2.1, tilt: (r() - 0.5) * 0.07, ph: r() * TAU });
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = 0;
    for (const q of S) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y - dx * q.tilt - Math.sin(dx / q.len * 2.6 + q.ph) * 1.3, v = 1 - (dx / q.len) ** 2 - (dy / q.th) ** 2;
      if (v > best) best = v;
    }
    D[y * W + x] = best * (0.7 + 0.6 * vnoise(x / 8, y / 1.5, seed, 60)) + (vnoise(x / 3, y, seed + 1, 160) - 0.5) * 0.22;
  }
  const get = (x, y) => (y < 0 || y >= h) ? 0 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = D[y * W + x], th = 0.14 + (bayer(x, y) - 0.5) * 0.08;
    if (v <= th - 0.08) continue;
    if (v <= th) { if (bayer(x + 1, y + 2) < 0.35) p.set(x, y, pal.body, alpha * 0.5); continue; }
    const col = get(x, y + 1) <= th ? pal.bot : get(x, y - 1) <= th ? pal.top : pal.body;
    p.set(x, y, col, v > th + 0.2 ? alpha : alpha * 0.8);
  }
  return p.done();
}
const WISP_DEFS = [
  { seed: 21, y: 4, h: 24, n: 9, k: 1, pal: { top: '#5f6a9e', body: '#8a80ae', bot: '#d89aa0' }, a: 220 },
  { seed: 33, y: 30, h: 26, n: 8, k: 2, pal: { top: '#6f7aa6', body: '#a88aa4', bot: '#f4ac92' }, a: 225 },
  { seed: 47, y: 56, h: 30, n: 9, k: 3, pal: { top: '#565c8c', body: '#6c6a94', bot: '#ffc8a0' }, a: 235 },
];

/* ================= The hot-air balloon (far off, behind the roofs and the masts) ================= */
const BAL = { w: 23, h: 32 };
/** crew: the heads over the basket's rim (2 = the pilot and a passenger, 1 = the pilot alone, for crowd 0) */
function renderBalloon(lit, crew = 2) {
  const p = pix(BAL.w, BAL.h), cx = 11;
  const hw = j => j <= 11 ? Math.sqrt(Math.max(0, 121 - (11 - j) ** 2)) : lerp(11, 2.4, Math.pow((j - 11) / 11, 1.35));
  for (let j = 0; j <= 22; j++) {
    const w = hw(j);
    for (let i = Math.ceil(cx - w); i <= Math.floor(cx + w); i++) {
      const u = (i - cx) / Math.max(1, w), g = Math.floor((Math.asin(clamp(u, -1, 1)) / Math.PI + 0.5) * 5.999);   // six gores, narrower at the rim
      let col = g % 2 ? '#e9dcbc' : '#d8482e';
      if (j >= 9 && j <= 11) col = g % 2 ? '#2f6aa8' : '#f0c040';                              // a band round the widest part
      col = mixHex(col, '#1c2756', 0.5 - 0.3 * u - 0.012 * j + (j < 4 ? 0.1 : 0));           // dusk: the left side keeps the glow
      if (lit) col = mixHex(col, '#ffd070', clamp((j - 6) / 16, 0, 1) * 0.75 * (1 - Math.abs(u) * 0.5));
      p.set(i, j, col);
    }
  }
  for (let j = 23; j <= 26; j++) { p.set(cx - 2, j, '#1a1c2c'); p.set(cx + 2, j, '#1a1c2c'); }   // the lines
  if (lit) { p.set(cx, 23, '#fff4c0'); p.set(cx, 24, '#ffb040'); p.set(cx, 22, '#fff4c0'); }
  for (let j = 27; j <= 30; j++) for (let i = cx - 2; i <= cx + 2; i++) p.set(i, j, j === 27 ? '#8a6a44' : (i + j) % 2 ? '#5a3e26' : '#6e4e30');
  p.set(cx - 1, 26, '#e8b890'); if (crew > 1) p.set(cx + 1, 26, '#e8b890');                      // two heads over the basket's rim
  return p.done();
}
/** burner bursts: about every 9 s, 1.6 s long (loop-safe: 80 steps of 3 s) */
const burner = t => { const s = step(t, 80); return hash(s, 71) < 0.36 && frac(t / 3) < 0.55; };
function balloonAt(t) {
  if (t < BALLOON.t0 || t >= BALLOON.t1) return null;
  const u = (t - BALLOON.t0) / (BALLOON.t1 - BALLOON.t0);
  return { x: lerp(-30, W + 8, u), y: 36 - 14 * Math.sin(Math.PI * u) + 3 * Math.sin(u * 11) };
}

/* ================= Gulls and swifts ================= */
const GULL_KEY = { w: '#e4e9f4', g: '#9aa6c4', k: '#2a3050', y: '#f0b040' };
const GULL_F = [
  ['k.......k', '.gw...wg.', '..wwwww..', '....wy...'],
  ['.........', 'kgww.wwgk', '..wwwww..', '....wy...'],
  ['.........', '..wwwww..', '.gwwwywg.', 'k.......k'],
];
const GULLS = [
  { cx: 250, cy: 34, ax: 210, ay: 14, kx: 3, ky: 7, px: 0.1, py: 0.2 },
  { cx: 150, cy: 52, ax: 170, ay: 12, kx: 4, ky: 9, px: 0.55, py: 0.7 },
  { cx: 330, cy: 26, ax: 150, ay: 10, kx: 5, ky: 11, px: 0.8, py: 0.4 },
  { cx: 240, cy: 120, ax: 260, ay: 30, kx: 2, ky: 5, px: 0.3, py: 0.9 },     // one low, in front of the houses and the masts
];
const SWIFT_F = [['k...k', '.kkk.'], ['.kkk.', 'k...k']];
const SWIFTS = [];
{
  const r = rng(808);
  for (let i = 0; i < 11; i++) SWIFTS.push({ cx: 250 + r() * 190, cy: 30 + r() * 30, ax: 34 + r() * 50, ay: 8 + r() * 14,
    k: (r() < 0.5 ? -1 : 1) * (14 + Math.floor(r() * 10)), ph: r(), k2: 3 + Math.floor(r() * 4) });
}
function birdAt(b, t) {
  const a = TAU * (b.kx * t / LOOP + b.px);
  return { x: b.cx + b.ax * Math.sin(a), y: b.cy + b.ay * Math.sin(TAU * (b.ky * t / LOOP + b.py)), vx: Math.cos(a) };
}

/* ================= The sunny side: plastered, painted fronts, white window frames, red tile roofs with dormers ================= */
const TILE = ['#3a1c2a', '#57262c', '#763430', '#934436', '#ab563e', '#bf6a48'];
const FRAME = ['#6a769c', '#8894b6', '#aab4cf', '#c8cbd6', '#e6dcc6', '#fbeac8'];
const GLASS = ['#4a609a', '#3c5088', '#2f4076', '', '#222e5c', '#1c264e', '#182044'];   // unlit panes mirror the sky (row 3: the bar)
const LIT_PAIRS = [['#ffe9b0', '#f6c06c'], ['#ffdc94', '#eca452'], ['#fff3d0', '#f0d08c'], ['#f6b878', '#cf7a3e'], ['#e2e8f6', '#aebcda']];
/** a facade colour seen at dusk: 0 deep blue shadow .. 0.5 the paint under the bright night sky .. 1 warm lamplight */
function toneRamp(base) {
  const b = C(base), out = [];
  for (let i = 0; i < 6; i++) {
    const v = i / 5, k = v < 0.5 ? lerp(0.48, 0.82, v * 2) : lerp(0.82, 0.98, (v - 0.5) * 2);
    const cool = v < 0.5 ? lerp(0.5, 0.1, v * 2) : lerp(0.1, 0, (v - 0.5) * 2), warm = v > 0.5 ? (v - 0.5) * 0.5 : 0;
    out.push(toHex(...b.map((ch, j) => Math.round(lerp(lerp(ch * k, [30, 42, 96][j], cool), [255, 214, 150][j], warm)))));
  }
  return out;
}
/** warm light on the fronts: the terraces' lamps and heaters below, the quay lanterns */
function warmAt(x, y) {
  let s = 0.3 * smooth(102, 148, y);
  for (const lx of FAR_LAMPS) s += 0.2 * Math.exp(-Math.hypot((x - lx) / 20, (y - 146) / 22));
  return s;
}
const WINS = [];                                    // every window that can light up: { x, y, w, h, mode, k, ph, duty, c1, c2 }
const SIGNS = [];                                   // the shops' fascias { x0, x1, y0, y1 }: the rigging passes behind them
const inSign = (x, y) => SIGNS.some(s => x >= s.x0 && x <= s.x1 && y >= s.y0 && y <= s.y1);
/** a shop's name sits centred on its fascia unless a mast (2 px wide) stands in front of it: then it moves along the
    fascia to the nearest stretch the masts leave clear */
function signX(h) {
  const tw = textWidth(h.shop), c = h.x0 + ((h.w - tw) >> 1), masts = SHIPS.flatMap(S => S.masts.map(m => Math.round(S.x0 + m.u * S.len)));
  for (let d = 0; d < h.w; d++) for (const x of [c - d, c + d]) {
    if (x >= h.x0 + 2 && x + tw <= h.x0 + h.w - 2 && masts.every(m => m + 1 < x - 1 || m > x + tw)) return x;
  }
  return c;
}
function addWin(r, x, y, w, h, force) {
  const v = r(), mode = force || (v < 0.4 ? 'on' : v < 0.72 ? 'tog' : 'off'), lp = LIT_PAIRS[Math.floor(r() * r() * 5)];
  WINS.push({ x, y, w, h, mode, k: 2 + Math.floor(r() * 3), ph: r(), duty: 0.35 + r() * 0.35, c1: lp[0], c2: lp[1] });
}
function renderRow() {
  const p = pix(W, HB + 1), r = rng(1681);
  for (const h of HOUSES) {
    const ramp = toneRamp(h.col), { x0, w, eave, ridge, base } = h, y0 = base - SH;
    const wall = (x, y, dv = 0) => pick(ramp, 0.44 + 0.1 * (y - eave) / (HB - eave) + warmAt(x, y) + (vnoise(x / 3, y / 2.5, h.no) - 0.5) * 0.08 + dv, x, y, 0.6);
    const frame = (x, y, dv = 0) => pick(FRAME, 0.42 + warmAt(x, y) * 1.3 + dv, x, y, 0.5);
    /** a gable's outline, rows j = 0.. up from row yb: half-width hw(j) about column c (a pixel is in when |x - c| <= hw);
        plastered inside, white stone on the edges (trim 'top': on the steps' treads only, their risers in shade) */
    const outline = (c, yb, rows, hw, trim) => {
      const ins = (x, j) => j >= 0 && j < rows && Math.abs(x - c) <= hw(j);
      for (let j = 0; j < rows; j++) for (let x = Math.ceil(c - hw(j)); x <= c + hw(j); x++) {
        const y = yb - j, tread = !ins(x, j + 1), riser = !ins(x - 1, j) || !ins(x + 1, j);
        p.set(x, y, tread || (riser && trim !== 'top') ? frame(x, y, tread ? 0.2 : x < c ? 0.14 : -0.12) : riser ? wall(x, y, x < c ? 0.04 : -0.18) : wall(x, y, 0.02));
      }
    };
    // the roof, seen from the front: courses of red tiles, a dark ridge, chimneys against the sky
    if (!h.gable) for (let y = ridge; y < eave; y++) for (let u = 0; u < w; u++) {
      const x = x0 + u, f = (y - ridge) / (h.rh || RH);
      let v = 0.3 + 0.38 * f + (hash(x >> 1, y >> 1, h.no) - 0.5) * 0.16 + ((y - ridge) % 3 === 2 ? -0.12 : 0);
      if (y === ridge) v = 0.14; else if (u === 0 || u === w - 1) v -= 0.2;
      p.set(x, y, pick(TILE, v, x, y, 0.7));
    }
    if (!h.gable) for (const f of h.bays > 4 ? [0.22, 0.74] : [0.3]) {
      const cx = x0 + Math.round(w * f);
      for (let y = ridge - 7; y <= ridge + 1; y++) for (let x = cx; x < cx + 5; x++)
        p.set(x, y, y === ridge - 7 ? '#2a1c2a' : x === cx ? '#6a4448' : y === ridge - 6 ? '#4a2c36' : '#3c2430');
      p.set(cx + 1, ridge - 9, '#2a1c2a'); p.set(cx + 1, ridge - 8, '#2a1c2a'); p.set(cx + 3, ridge - 8, '#2a1c2a');
    }
    // the front
    for (let y = eave; y < HB; y++) for (let u = 0; u < w; u++) p.set(x0 + u, y, wall(x0 + u, y, u === 0 ? -0.16 : 0));
    for (let u = 0; u < w; u++) {                                                     // white cornice under the eaves
      p.set(x0 + u, eave, frame(x0 + u, eave, 0.18)); p.set(x0 + u, eave + 1, frame(x0 + u, eave + 1, u % 3 ? 0 : -0.2));
      p.set(x0 + u, eave + 2, wall(x0 + u, eave + 2, -0.2));
      if (h.band) p.set(x0 + u, y0 - 1, frame(x0 + u, y0 - 1, 0.05));
    }
    for (let y = eave + 2; y < base; y++) p.set(x0 + w - 1, y, '#2a2e4a');            // downpipe
    // dormers
    for (let i = 0; i < (h.dorm || 0); i++) {
      const cx = x0 + Math.round(w * (i + 0.5) / h.dorm), top = eave - 12;
      for (let j = 0; j < 3; j++) for (let x = cx - j; x <= cx + j; x++) p.set(x, top + j, x < cx ? '#7a3a34' : '#4a2028');
      for (let j = 3; j <= 9; j++) for (let x = cx - 3; x <= cx + 3; x++) {
        const edge = Math.abs(x - cx) === 3;
        p.set(x, top + j, edge ? (x < cx ? '#5a2a2e' : '#3a1a24') : frame(x, top + j, j === 3 ? 0.1 : -0.08));
      }
      for (let j = 0; j < 4; j++) for (let x = cx - 1; x <= cx + 1; x++) p.set(x, top + 5 + j, GLASS[j + (j > 2 ? 1 : 0)]);
      addWin(r, cx - 1, top + 5, 3, 4);
    }
    // a gabled wall dormer: the front carried up through the roof (No. 9's: two bays, a pediment, a hoist beam under its peak)
    const bays = [];
    if (h.kvist) {
      const { at, n, top: form } = h.kvist, kx0 = x0 + 1 + at * BAY, kx1 = kx0 + n * BAY, mid = (kx0 + kx1) >> 1, top = eave - 13;
      for (let y = top; y < eave; y++) for (let x = kx0; x <= kx1; x++) p.set(x, y, y === top ? frame(x, y, 0.2) : wall(x, y, x === kx0 ? -0.14 : 0.02));
      if (form === 'pitch') {
        for (let j = 1; j <= Math.min(8, n * BAY / 2 + 1); j++) for (let x = kx0 - 1 + j; x <= kx1 + 1 - j; x++) {
          const edge = x === kx0 - 1 + j || x === kx1 + 1 - j;
          p.set(x, top - j, edge ? frame(x, top - j, x < mid ? 0.2 : -0.05) : wall(x, top - j, 0.03));
        }
        if (n > 1) {
          p.set(mid, top - 4, '#ffd88a'); p.set(mid + 1, top - 4, '#f0b060');                        // a little round window
          p.set(mid, top - 9, '#3a2a20'); p.set(mid + 1, top - 9, '#3a2a20'); p.set(mid + 1, top - 8, '#8a8a92');   // the hoist beam
        }
      } else {                                                                        // curved: shoulders swept in to a round head
        const c = (kx0 + kx1) / 2, half = (kx1 - kx0) / 2 + 1;
        outline(c, top - 1, 11, j => j < 6 ? 3.5 + (half - 3.5) * (1 - smooth(0, 6, j)) : j === 6 ? 4.5 : 3.5 * Math.sqrt(Math.max(0, 1 - ((j - 6.5) / 4.5) ** 2)));
        p.set(mid, top - 8, '#ffd88a'); p.set(mid + 1, top - 8, '#f0b060'); p.set(mid, top - 9, '#fff0c0'); p.set(mid + 1, top - 9, '#ffd88a');   // an oval window
      }
      for (let b = at; b < at + n; b++) bays.push([x0 + 3 + b * BAY, top + 3]);
    }
    // a front of its own: the attic storey in the gable, a loading hatch over it, the hoist beam at the top
    if (h.gable) {
      const c = x0 + (w - 1) / 2, half = (w - 1) / 2, step = h.gable === 'step';
      const rows = step ? 21 : 24, hw = step ? j => half - 2 * (Math.floor(j / 3) + 1)
        : j => j < 15 ? 5.5 + (half - 5.5) * (1 - smooth(0, 15, j)) : j < 20 ? (j === 19 ? 6.5 : 5.5) : 5.5 * Math.sqrt(Math.max(0, 1 - ((j - 19) / 5) ** 2));
      outline(c, eave - 1, rows, hw, step ? 'top' : 'all');
      const hx = Math.round(c) - 1, hy = eave - (step ? 17 : 18);
      for (let y = hy; y < hy + 4; y++) for (let x = hx; x < hx + 3; x++) p.set(x, y, y === hy ? frame(x, y, -0.1) : x === hx + 1 ? '#2a1c1c' : '#4a2e26');   // the hatch's doors
      p.set(hx + 1, eave - rows + 1, '#3a2a20'); p.set(hx + 1, eave - rows + 2, '#3a2a20'); p.set(hx + 2, eave - rows + 2, '#3a2a20'); p.set(hx + 2, eave - rows + 3, '#8a8a92');   // the hoist beam, its hook
      for (let b = 1; b <= 2; b++) bays.push([x0 + 3 + b * BAY, eave - 11]);
    }
    // windows, floor by floor
    for (let i = 1; i < h.st; i++) for (let b = 0; b < h.bays; b++) bays.push([x0 + 3 + b * BAY, base - (i + 1) * SH + 5]);
    for (const [gx, gy] of bays) {
      for (let y = gy - 1; y <= gy + 7; y++) for (let x = gx - 1; x <= gx + 4; x++) {
        const glass = x >= gx && x < gx + 4 && y >= gy && y < gy + 7, j = y - gy;
        p.set(x, y, glass && j !== 3 ? GLASS[j] : frame(x, y, glass ? -0.22 : x === gx - 1 ? 0.1 : 0));
      }
      for (let x = gx - 1; x <= gx + 4; x++) { p.set(x, gy + 8, frame(x, gy + 8, 0.14)); p.set(x, gy + 9, wall(x, gy + 9, -0.14)); }
      addWin(r, gx, gy, 4, 7, h.kvist && gy < eave ? 'on' : undefined);
    }
    // the ground floor: a fascia with the place's name, lit shop windows, a door
    for (let y = y0; y < base; y++) for (let u = 1; u < w - 1; u++) {
      const x = x0 + u, j = y - y0;
      let col;
      if (j < 1) col = wall(x, y, -0.1);
      else if (j < 8) col = mixHex(h.fas, '#ffd9a0', 0.12 * warmAt(x, y));
      else if (u % BAY < 2 || j === 8) col = wall(x, y, -0.06);
      else col = j < 11 ? '#ffe6a8' : j < 14 ? '#f8c474' : '#e09a4c';
      p.set(x, y, col);
    }
    const door = x0 + 2 + BAY * (h.no % 3 === 0 ? 1 : h.bays - 2);
    for (let y = y0 + 9; y < base; y++) for (let x = door; x < door + 6; x++) p.set(x, y, y < y0 + 11 ? '#ffe2a0' : x === door || x === door + 5 ? '#1c1418' : '#3a2620');
    const sx = signX(h);
    p.text(h.shop, sx, y0 + 2, h.ink || '#f2cf80');
    SIGNS.push({ x0: x0 + 1, x1: x0 + w - 2, y0: y0 + 1, y1: y0 + 7 });
    // the basement band: cellar restaurants, their low windows glowing
    for (let y = base; y < HB; y++) for (let u = 0; u < w; u++) {
      const x = x0 + u, lit = y > base && y < HB - 1 && u % BAY > 2 && u % BAY < 7;
      p.set(x, y, lit ? (y === base + 1 ? '#ffd890' : '#f0a850') : pick(['#3c4058', '#565a70', '#7a7480', '#a08c7c'], 0.3 + warmAt(x, y) * 1.4, x, y));
    }
    if (h.no === 11) {                                                              // the sugar-baker over No. 11's gateway
      const gx = x0 + w - 9;
      for (let y = y0 + 8; y < HB; y++) for (let x = gx; x < gx + 6; x++) p.set(x, y, y === y0 + 8 && (x === gx || x === gx + 5) ? '#2a1a12' : '#161019');
      for (const [dx, dy, c] of [[2, 1, '#f4e6c4'], [2, 2, '#e8d0a0'], [1, 3, '#f4e6c4'], [2, 3, '#f4e6c4'], [3, 3, '#f4e6c4'], [4, 2, '#ffffff'], [4, 1, '#ffffff'],
        [2, 4, '#e8d0a0'], [2, 5, '#c8b080'], [3, 5, '#c8b080']]) p.set(gx + dx, y0 + 1 + dy, c);
    }
    if (h.no === 15) for (const [dx, dy] of [[1, 0], [2, 0], [3, 0], [4, 0], [0, 1], [1, 1], [2, 1], [3, 1], [4, 1], [0, 2], [1, 2], [4, 2]])
      p.set(x0 + w - 9 + dx, y0 - 5 + dy, dx === 0 ? '#8a8a98' : '#b4b4c0');           // the elephant over No. 15's door
  }
  paintSideStreet(p);
  return p.done();
}
/** Lille Strandstraede: a narrow street running north between No. 17 and No. 19, a lamp hung on a wire across it */
function paintSideStreet(p) {
  const L = toneRamp(HOUSES[7].col), R = toneRamp(HOUSES[8].col), { x0, x1 } = SIDE;
  for (let x = x0; x < x1; x++) for (let y = Math.min(HOUSES[7].eave, HOUSES[8].eave); y < HB; y++) {
    const u = x - x0, v = x1 - 1 - x, lTop = HOUSES[7].eave + u * 6, rTop = HOUSES[8].eave + v * 7;
    let col = null;
    if (u < 5 && y >= lTop) col = y === lTop ? '#3a2630' : pick(L, 0.1 + 0.25 * smooth(120, 150, y) - u * 0.02, x, y, 0.4);   // No. 17's gable wall, in shade
    else if (v < 5 && y >= rTop) col = y === rTop ? '#4a3640' : pick(R, 0.3 + 0.3 * smooth(120, 150, y) - v * 0.03, x, y, 0.4);
    else if (y >= 118) {                                                             // a house across the far end, hazy
      col = y < 121 ? '#5c4458' : pick(['#6a6890', '#7c789a', '#9488a0'], 0.2 + (y - 121) / 50, x, y, 0.4);
      if (y >= 126 && y <= 129 && (u === 6 || u === 9)) col = u === 6 ? '#ffd88a' : '#39457a';
      if (y >= 135 && y <= 138 && (u === 6 || u === 9)) col = u === 9 ? '#ffe2a0' : '#39457a';
      if (y >= 144) col = pick(['#56546c', '#786c72', '#a08670', '#c8a070'], 0.2 + (y - 144) / 8 + (u > 5 && u < 10 ? 0.25 : 0), x, y, 0.5);
    }
    if (col) p.set(x, y, col);
  }
  for (let x = x0 + 4; x < x1 - 4; x++) p.set(x, 123 + (Math.abs(x - 330) < 2 ? 1 : 0), '#1c2040');   // the wire
  p.set(329, 125, '#2a2c44'); p.set(330, 125, '#2a2c44'); p.set(329, 126, '#fff2c8'); p.set(330, 126, '#fff2c8'); p.set(329, 127, '#ffd890'); p.set(330, 127, '#ffd890');
}

/* ================= The north quay: cobbles, granite wall, restaurant terraces under parasols, lanterns ================= */
const PARASOLS = [];                                // { x, col, dark } canopy centres along the terraces
for (let x = 13; x < W; x += 23) {
  if (x > SIDE.x0 - 12 && x < SIDE.x1 + 12) continue;
  const h = HOUSES.find(q => x >= q.x0 && x < q.x0 + q.w) || HOUSES[0], k = h.no % 4;
  PARASOLS.push({ x, col: ['#e8dcc0', '#8a2a34', '#e8dcc0', '#2a4a3e'][k], dark: k % 2 === 1 });
}
const CANDLES = [];                                 // table candles: flicker per frame
const TOASTERS = [];                                // seated people who lift a glass now and then: { x, y, rank }
const BLANKETS = ['#b8323a', '#8a8e9e', '#c8a24a', '#3a5a8a', '#d8d0c0', '#5a7a4a', '#a85a7a'];
// The diners are painted into the quay (and the quay is mirrored in the canal), so a terrace table is the unit that
// thins, in TERR_CLS steps spread along the quay: class c has rank (c + 0.5) / TERR_CLS. One north quay and one
// mirror are prerendered per number of classes at their tables (QUAY_LV, MIRROR_LV).
const TABLES = [];                                  // { x, w, cls }: the columns of a table's diners, its class
const DINER_Y = 149, DINER_H = 7;                   // the rows the seated diners take up
const TERR_CLS = 10;
const terrRank = c => (c + 0.5) / TERR_CLS;
const terrLevel = crowd => { let n = 0; while (n < TERR_CLS && terrRank(n) < crowd) n++; return n; };
/** people: false leaves the diners out (the empty terraces the thinner levels start from); the lists are filled by the full one */
function renderFarQuay(people = true) {
  const p = pix(W, H), r = rng(5150);
  let ti = 0;
  const pool = (x, y) => Math.min(1, warmAt(x, 150) * 1.5 + 0.25 * Math.exp(-(((y - 155) / 6) ** 2)));
  for (let y = HB; y < WL; y++) for (let x = 0; x < W; x++) {
    let col;
    if (y < FEDGE) col = pick(['#3a3c52', '#54505e', '#74665e', '#987c60', '#bc9662'], 0.2 + 0.62 * pool(x, y) + (hash(x >> 1, y, 5) - 0.5) * 0.16, x, y);
    else if (y === FEDGE) col = pick(['#6a6e84', '#8a8a94', '#b0a694'], 0.3 + pool(x, y) * 0.6, x, y);
    else if (y === WL - 1) col = '#1c2a34';
    else col = ((x + ((y - FEDGE) >> 1 & 1) * 5) % 10 === 0 || (y - FEDGE) % 2 === 1 && hash(x, y, 9) < 0.25) ? '#262a40' : pick(['#343850', '#42465e', '#52546a'], 0.3 + hash(Math.floor((x + ((y - FEDGE) >> 1 & 1) * 5) / 10), y >> 1, 6) * 0.6, x, y);
    p.set(x, y, col);
  }
  for (let x = 30; x < W; x += 55) { p.set(x, FEDGE + 3, '#7a7e90'); p.set(x - 1, FEDGE + 4, '#55596c'); p.set(x + 1, FEDGE + 4, '#55596c'); p.set(x, FEDGE + 5, '#55596c'); }   // mooring rings
  // terraces: tables with a candle, people wrapped in blankets, then the parasols over them
  for (const P of PARASOLS) for (const side of [-1, 1]) {
    const tx = P.x + side * 6;
    for (let x = tx - 2; x <= tx + 2; x++) p.set(x, 154, '#c8a878');
    p.set(tx, 155, '#5a4634'); p.set(tx, 156, '#5a4634');
    const cls = Math.floor(rankOf(ti++, 0.4) * TERR_CLS), rank = terrRank(cls), x0 = Math.max(0, tx - 5);
    if (people) { CANDLES.push({ x: tx, y: 153 }); TABLES.push({ x: x0, w: Math.min(W, tx + 6) - x0, cls }); }
    for (const s2 of [-1, 1]) {
      if (r() < 0.14) continue;
      const hx = tx + s2 * 4, hair = ['#2a1a14', '#6a4a2a', '#d8b46a', '#1a1418', '#8a3a22', '#c8c4c0'][Math.floor(r() * 6)];
      const bl = BLANKETS[Math.floor(r() * BLANKETS.length)], toasts = r() < 0.45;
      if (!people) continue;
      p.set(hx, 149, hair); p.set(hx, 150, '#e8b890');
      for (let y = 151; y <= 155; y++) { p.set(hx, y, bl); p.set(hx + s2, y, y < 153 ? bl : mixHex(bl, '#10142c', 0.4)); }
      p.set(hx - s2, 152, mixHex(bl, '#ffffff', 0.18));
      if (toasts) TOASTERS.push({ x: hx - s2, y: 151, rank });
    }
  }
  for (let x = 2; x < W; x += 5) {                                                   // planter boxes along the terraces' front
    if ((x > SIDE.x0 - 6 && x < SIDE.x1 + 4) || hash(x, 77) < 0.22) continue;
    p.set(x, 157, '#3a2c28'); p.set(x + 1, 157, '#3a2c28'); p.set(x + 2, 157, '#3a2c28');
    p.set(x, 156, '#2a5a34'); p.set(x + 1, 156, hash(x, 78) < 0.4 ? '#e86a8a' : '#3a7a40'); p.set(x + 2, 156, '#22482c');
  }
  for (const P of PARASOLS) {
    const lit = P.dark ? mixHex(P.col, '#ffb060', 0.35) : '#f6e2b4', top = P.dark ? mixHex(P.col, '#1c2756', 0.3) : mixHex(P.col, '#5a6aa0', 0.3);
    for (let j = 0; j < 4; j++) { const hw = [3, 6, 8, 9][j]; for (let x = P.x - hw; x <= P.x + hw; x++) p.set(x, 142 + j, j === 3 ? lit : x < P.x - hw + 2 ? mixHex(top, '#ffffff', 0.2) : top); }
    for (let x = P.x - 9; x <= P.x + 9; x += 2) p.set(x, 146, lit);
    p.set(P.x, 141, '#2a2c44');
    for (let y = 146; y <= 157; y++) p.set(P.x, y, y < 149 ? '#fff2c8' : '#2a2630');                  // the heater lamp under the canopy, the pole
  }
  for (const lx of FAR_LAMPS) {                                                      // quay lanterns
    for (let y = 146; y < FEDGE; y++) p.set(lx, y, '#141826');
    p.set(lx - 1, 161, '#141826'); p.set(lx + 1, 161, '#141826');
    for (let x = lx - 1; x <= lx + 1; x++) { p.set(x, 141, '#1c2032'); for (let y = 142; y <= 145; y++) p.set(x, y, x === lx ? '#fff8e0' : '#ffd890'); }
    p.set(lx, 140, '#1c2032');
  }
  return trim(p);
}
/** the north quay with the tables of the first n classes taken: the empty terraces plus those tables' diners from the full one */
function quayLevel(empty, n) {
  const [c, x] = makeCanvas(empty.c.width, empty.c.height);
  x.drawImage(empty.c, 0, 0);
  for (const T of TABLES) if (T.cls < n) x.drawImage(QUAY_C.c, T.x - QUAY_C.x, DINER_Y - QUAY_C.y, T.w, DINER_H, T.x - empty.x, DINER_Y - empty.y, T.w, DINER_H);
  return { c, x: empty.x, y: empty.y };
}

/* ================= The roof sign on No. 7 (the city's one billboard): a steel frame on the ridge, three lamps ================= */
const STEEL = { d: '#10142a', m: '#1c2240', h: '#343c62', l: '#6a6e8e', w: '#a08c7a' };
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, R = BB.x + BB.w + 1, T = BB.y - 2, B = BB.y + BB.h + 1, ridge = SIGN_HOUSE.ridge;
  for (const lx of [BB.x + 5, BB.x + BB.w - 6]) {                                    // legs down to the ridge, braced
    for (let y = B; y <= ridge + 2; y++) { p.set(lx, y, STEEL.m); p.set(lx + 1, y, STEEL.d); }
    p.line(lx - 5, ridge + 2, lx, B + 1, STEEL.h); p.line(lx + 6, ridge + 2, lx + 1, B + 1, STEEL.d);
  }
  p.line(L - 6, ridge + 1, L, T + 4, STEEL.m); p.line(R + 6, ridge + 1, R, T + 4, STEEL.d);   // stays from the top corners
  for (let y = T; y <= B; y++) for (let x = L; x <= R; x++) {
    let col = STEEL.m;
    if (y === T) col = x % 6 === 2 ? STEEL.w : STEEL.l; else if (y === B) col = STEEL.d; else if (x === L) col = STEEL.h; else if (x === R) col = STEEL.d;
    p.set(x, y, col);
  }
  for (const lx of BB_LAMPS) {                                                      // lamps on little arms over the top edge
    p.set(lx, T - 1, STEEL.h); p.set(lx, T - 2, STEEL.m);
    for (let x = lx - 2; x <= lx + 2; x++) { p.set(x, BB_LAMP_Y - 1, x < lx ? STEEL.l : STEEL.h); p.set(x, BB_LAMP_Y, Math.abs(x - lx) < 2 ? '#fff6d8' : '#ffe2a0'); }
  }
  return trim(p);
}
const ENAMEL = ['#6c7090', '#8e8e9e', '#b4aca6', '#d6c8ae', '#ece0c0', '#fbf0d4'];
function renderAd() {
  const { w, h } = BB, p = pix(w, h), lamps = BB_LAMPS.map(lx => lx - BB.x);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let cone = 0;
    for (const lx of lamps) { const dx = (i - lx) / (4 + j * 0.8); cone = Math.max(cone, Math.exp(-dx * dx) * Math.max(0, 1 - j / 15)); }
    p.set(i, j, pick(ENAMEL, 0.56 + 0.1 * (1 - j / h) + 0.3 * smooth(0.2, 0.6, cone), i, j, 0.7));
  }
  for (let i = 0; i < w; i++) { p.set(i, 0, '#b8322a'); p.set(i, h - 1, '#8a2420'); }
  for (let j = 0; j < h; j++) { p.set(0, j, '#b8322a'); p.set(w - 1, j, '#8a2420'); }
  for (let i = 2; i <= 14; i++) { p.set(i, 14, '#c8ccd8'); if (i > 2 && i < 14) p.set(i, 15, '#9aa0b4'); }   // the plate
  for (let i = 3; i <= 13; i++) { p.set(i, 12, i === 3 || i === 13 ? '#3e2616' : '#6a4426'); p.set(i, 13, '#3e2616'); }   // rye bread
  const c = p.done();
  sign(p.x, 'SMØRRE', 17, 3, '#1c2c6a');
  sign(p.x, 'BRØD', 21, 10, '#c8322a');
  return c;
}
// what lands on the bread, a new layer every 5 s (the sixth step shows it finished): [dx, dy, colour] from the plate's left
const TOPPINGS = [
  [[3, 11, '#6aaa3e'], [4, 10, '#4c8a30'], [5, 11, '#6aaa3e'], [6, 11, '#4c8a30'], [7, 10, '#6aaa3e'], [8, 11, '#4c8a30'], [9, 11, '#6aaa3e'], [10, 10, '#4c8a30'], [11, 11, '#6aaa3e'], [12, 11, '#4c8a30'], [13, 11, '#6aaa3e']],
  [[4, 9, '#fbf6e8'], [5, 9, '#f6c030'], [6, 9, '#fbf6e8'], [8, 9, '#fbf6e8'], [9, 9, '#f6c030'], [10, 9, '#fbf6e8'], [12, 9, '#fbf6e8'], [12, 10, '#f6c030']],
  [[4, 8, '#f09080'], [5, 7, '#f6a898'], [6, 8, '#e87868'], [8, 8, '#f09080'], [9, 7, '#f6a898'], [10, 8, '#e87868'], [11, 7, '#f09080']],
  [[3, 9, '#f6e060'], [3, 8, '#fff2a0'], [7, 6, '#3a8a3a'], [7, 7, '#2a6a2e'], [12, 6, '#3a8a3a'], [12, 8, '#2a6a2e']],
  [[8, 6, '#8a6a44'], [8, 5, '#8a6a44'], [8, 4, '#8a6a44'], [8, 3, '#8a6a44'], [8, 2, '#8a6a44'], [9, 2, '#d8303a'], [10, 2, '#f8f4ec'], [11, 2, '#d8303a'], [12, 2, '#d8303a'],
    [9, 3, '#f8f4ec'], [10, 3, '#f8f4ec'], [11, 3, '#f8f4ec'], [12, 3, '#f8f4ec'], [9, 4, '#d8303a'], [10, 4, '#f8f4ec'], [11, 4, '#d8303a'], [12, 4, '#d8303a']],
];

/* ================= The veteran harbour: wooden ships moored along the north quay, gaff-rigged, sails furled ================= */
const DECK = 159;                                   // the ships' rails
// Rigging is thin, part-transparent line: dark against the sky, faint where it crosses the painted fronts (they show
// through), and behind the masts, the spars and the shop signs, so the row never looks caged and every name stays whole.
// A mast has one shroud a side; a ship has a forestay, a backstay and a stay between her mastheads.
const RIG_SKY = '#232846', RIG_FRONT = '#2c2c4a', RIG_A_SKY = 205, RIG_A_FRONT = 118;
let ROW_A;                                          // the house row's alpha (set in prerender): what a rope is seen against
// bow: +1 points east (right). masts: u along the hull, top row. dressed: signal flags along the stays (midsummer)
const SHIPS = [
  { x0: 2, len: 68, bow: -1, hull: '#1c2030', strake: '#e8e0d0', masts: [{ u: 0.32, top: 36 }, { u: 0.75, top: 30 }] },
  { x0: 146, len: 78, bow: 1, hull: '#22402e', strake: '#e8c860', masts: [{ u: 0.24, top: 30 }, { u: 0.66, top: 22 }] },
  { x0: 236, len: 74, bow: 1, hull: '#e8e4d8', strake: '#2a4a8a', masts: [{ u: 0.3, top: 32 }, { u: 0.72, top: 26, hoist: true }] },
  { x0: 346, len: 66, bow: 1, hull: '#5a1c20', strake: '#f0ece0', masts: [{ u: 0.26, top: 38 }, { u: 0.68, top: 30 }], dressed: true },
  { x0: 430, len: 48, bow: -1, hull: '#1c2c4e', strake: '#e8c860', masts: [{ u: 0.5, top: 44 }] },
];
const FLAG_COLS = ['#d8303a', '#f6d040', '#2f5fb8', '#f4f0e8', '#2a2a34', '#e87a2a'];
function makeShip(S) {
  const hp = pix(W, H), rp = pix(W, H), { x0, len, bow } = S, x1 = x0 + len - 1, aft = -bow;
  const bowX = bow > 0 ? x1 : x0, sternX = bow > 0 ? x0 : x1;
  const front = (x, y) => y >= HB || (x >= 0 && x < W && y >= 0 && ROW_A[(y * W + x) * 4 + 3] > 0);
  const rope = (ax, ay, bx, by) => {
    const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay), 1);
    for (let k = 0; k <= n; k++) {
      const x = Math.round(lerp(ax, bx, k / n)), y = Math.round(lerp(ay, by, k / n));
      if (rp.alpha(x, y) === 255 || inSign(x, y)) continue;
      const f = front(x, y);
      rp.set(x, y, f ? RIG_FRONT : RIG_SKY, f ? RIG_A_FRONT : RIG_A_SKY);
    }
  };
  // hull: a sheer line, raked stem, counter stern
  for (let u = 0; u < len; u++) {
    const x = x0 + u, f = 2 * u / (len - 1) - 1, rail = DECK + 1 - Math.round(2.4 * f * f), toBow = bow > 0 ? len - 1 - u : u, toStern = len - 1 - toBow;
    for (let y = rail; y < WL; y++) {
      if (toBow < (y - rail) * 0.7 || toStern < (y - rail) * 0.3) continue;
      const j = y - rail;
      let col = j === 0 ? '#9a7850' : j === 1 ? S.strake : y >= WL - 2 ? '#5e2222' : mixHex(S.hull, '#0a0e22', 0.1 + j * 0.05);
      if (j === 3 && u % 9 === 4 && toBow > 8 && toStern > 6) col = '#ffd88a';                      // portholes
      hp.set(x, y, col);
    }
  }
  // deckhouse aft of the main mast, a skylight forward
  const hx = Math.round(x0 + len * (bow > 0 ? 0.36 : 0.52)), hw = Math.round(len * 0.14);
  for (let y = DECK - 6; y <= DECK; y++) for (let x = hx; x < hx + hw; x++)
    hp.set(x, y, y === DECK - 6 ? '#c8b89a' : (y === DECK - 4 || y === DECK - 3) && (x - hx) % 4 === 1 ? '#ffd88a' : x === hx ? '#8a7a66' : '#6a5a4c');
  S.lantern = { x: hx + hw + aft * 2, y: DECK - 9 };
  // bowsprit and flagstaff
  const tip = { x: bowX + bow * 13, y: DECK - 5 };
  rp.line(bowX - bow * 3, DECK - 1, tip.x, tip.y, '#7a5a3c');
  rp.line(sternX, DECK, sternX - bow * 3, DECK - 9, '#8a6a48');
  S.staff = { x: sternX - bow * 3, y: DECK - 9, dir: -bow };
  S.tops = []; S.masts2 = [];
  const tops = S.masts.map(m => ({ x: Math.round(x0 + m.u * len), y: m.top }));
  tops.forEach((m, i) => {
    const hounds = Math.round(m.y + 0.3 * (DECK - m.y)), boomY = DECK - 9, bl = Math.round(len * (tops.length > 1 ? 0.28 : 0.42));
    for (let y = hounds; y <= DECK; y++) { rp.set(m.x, y, '#b08a58'); rp.set(m.x + 1, y, '#5a4030'); }
    for (let y = m.y + 1; y < hounds; y++) rp.set(m.x, y, '#8a6a48');
    rp.set(m.x, m.y, '#f0e4c8');
    for (let x = m.x - 2; x <= m.x + 3; x++) rp.set(x, hounds, '#2a2438');                           // crosstrees
    for (const s of [-1, 1]) rope(m.x + (s > 0 ? 1 : 0), hounds + 1, m.x + s * 7, DECK - 1);        // shrouds
    const bx = m.x + (aft > 0 ? 2 : -1), be = bx + aft * bl;
    rp.line(bx, boomY, be, boomY, '#6a4c34');                                                       // boom
    const M = { x: m.x, top: m.y, hounds, boomY, aft, bl };
    S.masts2.push(M); S.tops.push({ x: m.x, y: m.y });
    if (S.masts[i].hoist) { S.sail = M; return; }                                                   // her mainsail is drawn per frame
    for (let q = 1; q < bl - 1; q++) {                                                              // furled sail, gaskets, the gaff on top
      const x = bx + aft * q, fat = q > 2 && q < bl - 4 ? 1 : 0;
      rp.set(x, boomY - 1, q % 5 === 2 ? '#5a4a3a' : '#b09a74'); rp.set(x, boomY - 2, q % 5 === 2 ? '#5a4a3a' : '#d8c8a0');
      if (fat) rp.set(x, boomY - 3, q % 5 === 2 ? '#5a4a3a' : '#e4d6b0');
      if (q < bl - 5) rp.set(x, boomY - 4, '#7a5a3c');
    }
  });
  // stays: to the bowsprit, between the mastheads, down to the stern; a furled jib on the forestay
  const fore = bow > 0 ? tops[tops.length - 1] : tops[0], main = bow > 0 ? tops[0] : tops[tops.length - 1];
  const stays = [[fore.x, fore.y + 1, tip.x, tip.y], [main.x, main.y + 1, sternX, DECK - 2]];
  if (tops.length > 1) stays.push([tops[0].x, tops[0].y + 2, tops[1].x, tops[1].y + 2]);
  for (let k = 0; k < 16; k++) {                                                                    // the furled jib on the forestay
    const u = 0.55 + k * 0.026, x = Math.round(lerp(fore.x, tip.x, u)), y = Math.round(lerp(fore.y + 1, tip.y, u) - 1);
    if (!inSign(x, y)) rp.set(x, y, k % 4 === 3 ? '#8a7a5a' : '#dccba4');
  }
  if (S.dressed) stays.forEach(([ax, ay, bx2, by2], si) => {                                        // signal flags, clear of the signs
    const n = Math.floor(Math.hypot(bx2 - ax, by2 - ay) / 6);
    for (let k = 1; k < n; k++) {
      const x = Math.round(lerp(ax, bx2, k / n)), y = Math.round(lerp(ay, by2, k / n)) + 1, c = FLAG_COLS[(k * 5 + si * 3) % FLAG_COLS.length];
      if (inSign(x, y) || inSign(x + 1, y) || inSign(x, y + 1) || inSign(x + 1, y + 1)) continue;
      rp.set(x, y, c); rp.set(x + 1, y, c); rp.set(x, y + 1, c); rp.set(x + 1, y + 1, (k + si) % 3 ? c : '#f4f0e8');
    }
  });
  for (const [ax, ay, bx2, by2] of stays) rope(ax, ay, bx2, by2);
  S.hullC = trim(hp); S.rig = trim(rp);
}

/* ================= The ketch's tanbark mainsail (hoisted for the evening, per frame) ================= */
const SAILC = ['#5e2a26', '#7c382c', '#9a4a34', '#b45c3e', '#cc7a4e'];
/** 0 furled .. 1 set */
function sailUp(t) {
  if (t < SAIL.up0 || t >= SAIL.dn1) return 0;
  if (t < SAIL.up1) return smooth(SAIL.up0, SAIL.up1, t);
  return t < SAIL.dn0 ? 1 : 1 - smooth(SAIL.dn0, SAIL.dn1, t);
}

/* ================= Things on the water: the tour boat, the jazz barge, kayaks, a picnic boat, swans ================= */
const COATS = ['#c8423a', '#2f5fa8', '#e8c040', '#3a8a5a', '#e8e0d0', '#7a4a8a', '#e87a3a', '#2a3a5a', '#d86a8a', '#5a9ab8', '#8a8e9e', '#1e2230'];
const HAIRS = ['#2a1a14', '#6a4a2a', '#d8b46a', '#1a1418', '#8a3a22', '#c8c4c0', '#4a3020', '#e8d08a'];
const SKINS = ['#f0c4a0', '#e8b890', '#d8a078', '#b07850', '#f4d0b0', '#8a5a3c'];
const TOUR = { w: 118, h: 22, seats: [] };
// Her passengers sit in TOUR_CLS classes spread along the boat; class c has rank c / TOUR_CLS, so the first class (rank 0:
// the party the guide always has) stays and the others thin. One boat is prerendered per number of classes aboard.
const TOUR_CLS = 8;
// (crowd 0: level 0, she sails with her skipper alone, no party and no guide)
const tourLevel = crowd => { if (nobody(crowd)) return 0; let n = 1; while (n < TOUR_CLS && n / TOUR_CLS < crowd) n++; return n; };
/** n: how many passenger classes are aboard (TOUR_CLS = everyone; that one also fills TOUR.seats; 0 = nobody, and no guide) */
function renderTourBoat(n = TOUR_CLS) {
  const p = pix(TOUR.w, TOUR.h), r = rng(404), w = TOUR.w;
  let pi = 0;
  for (let y = 14; y < 22; y++) for (let x = 0; x < w; x++) {
    if (w - 1 - x < (y - 14) * 1.3 || x < (y > 19 ? 1 : 0)) continue;                               // raked bow (right)
    p.set(x, y, y === 14 ? '#f4f0e4' : y === 15 ? '#d8d6d0' : y === 16 ? '#2f6ab8' : y === 17 ? '#f4f0e4' : y >= 20 ? '#16284e' : '#e4e0d4');
    if (y === 17 && x % 12 === 6 && x < w - 8) p.set(x, y, '#ffd060');
  }
  for (let row = 0; row < 2; row++) for (let x = 12 + row * 2; x < 94; x += 4) {                     // passengers, two staggered rows
    if (r() < 0.12) continue;
    const y = 7 + row * 2, coat = COATS[Math.floor(r() * COATS.length)], hair = HAIRS[Math.floor(r() * HAIRS.length)];
    const skin = SKINS[Math.floor(r() * 4)], cls = Math.floor(rankOf(pi++) * TOUR_CLS);
    if (cls >= n) continue;
    p.set(x, y, hair); p.set(x + 1, y, hair); p.set(x, y + 1, hair); p.set(x + 1, y + 1, skin);
    for (let yy = y + 2; yy < 14; yy++) for (let xx = x - 1; xx <= x + 2; xx++) p.set(xx, yy, xx === x + 2 ? mixHex(coat, '#ffe0b0', 0.25) : xx === x - 1 ? mixHex(coat, '#0a0c1c', 0.35) : coat);
    if (n === TOUR_CLS) TOUR.seats.push({ x, y, cls });
  }
  for (let y = 6; y < 14; y++) for (let x = 3; x <= 7; x++) p.set(x, y, y === 6 ? '#3a3e52' : y < 9 && x > 3 && x < 7 ? '#9ab8d8' : '#f4f0e4');   // the skipper's stand
  p.set(5, 4, '#1a1418'); p.set(5, 5, '#e8b890');
  p.line(1, 13, 1, 3, '#8a8a94');                                                                  // flagstaff at the stern
  for (let y = 3; y <= 5; y++) for (let x = 2; x <= 5; x++) p.set(x, y, y === 4 || x === 3 ? '#f8f4ec' : '#d8303a');
  if (n > 0) for (let y = 2; y < 14; y++) for (let x = 100; x <= 102; x++) p.set(x, y, y < 4 ? (x === 102 && y === 3 ? '#e8b890' : '#6a4a2a') : y < 10 ? '#d8303a' : '#1e2230');   // the guide, standing
  p.set(110, 13, '#fff8e0'); p.set(110, 12, '#8a8a94');
  return p.done();
}

/** the jazz barge (bow to the right), frame f of 2: a flat lighter, four poles with strings of bulbs, a trad band playing */
// (band: false leaves the band, its instruments and the dancers ashore: the bare barge of crowd 0)
const JZ = { w: 88, h: 42, deck: 34, poles: [5, 31, 57, 83], bulbs: [] };
function renderJazz(f, band = true) {
  const p = pix(JZ.w, JZ.h), { deck } = JZ, G = '#f0c040', GD = '#b8862a';
  for (let y = deck; y < JZ.h; y++) for (let x = 0; x < JZ.w; x++) {
    if (JZ.w - 1 - x < (y - deck) * 0.8 || x < (y - deck) * 0.4) continue;
    p.set(x, y, y === deck ? '#b08c5e' : y === deck + 1 ? '#e8c860' : y >= JZ.h - 2 ? '#5e2222' : '#1a1e30');
  }
  for (const px0 of JZ.poles) for (let y = 8; y < deck; y++) p.set(px0, y, '#4a3830');
  if (!f && band) for (let s = 0; s < 3; s++) for (let k = 1; k < 7; k++) {
    const u = k / 7, x = Math.round(lerp(JZ.poles[s], JZ.poles[s + 1], u)), y = Math.round(8 + 5 * Math.sin(Math.PI * u));
    JZ.bulbs.push({ x, y, c: (s * 6 + k) % 4 });
  }
  for (let s = 0; s < 3; s++) for (let x = JZ.poles[s]; x <= JZ.poles[s + 1]; x++) p.set(x, Math.round(7 + 5 * Math.sin(Math.PI * (x - JZ.poles[s]) / 26)), '#2a2438');
  if (!band) return p.done();
  /** a standing figure, feet on the deck: head, coat, legs; dy lifts it (the beat) */
  const fig = (x, coat, hair, dy = 0, hat = null) => {
    const y = deck - 14 - dy;
    p.set(x, y, hat || hair); p.set(x + 1, y, hat || hair); p.set(x + 2, y, hat || hair);
    p.set(x, y + 1, hair); p.set(x + 1, y + 1, '#e8b890'); p.set(x + 2, y + 1, '#e8b890'); p.set(x + 1, y + 2, '#d8a078'); p.set(x + 2, y + 2, '#e8b890');
    for (let j = 3; j <= 8; j++) for (let i = 0; i < 4; i++) p.set(x - 1 + i + (j === 3 && i === 0 ? 1 : 0), y + j, i === 3 ? mixHex(coat, '#ffe0b0', 0.3) : i === 0 ? mixHex(coat, '#0a0c1c', 0.4) : coat);
    for (let j = 9; j <= 13 + dy; j++) { p.set(x, y + j, '#1a1c2a'); p.set(x + 2, y + j, '#14161f'); }
  };
  // upright bass
  fig(12, '#e8e0d0', '#2a1a14', 0, '#1e2230');
  for (let j = 0; j < 10; j++) { const hw = [1, 2, 2, 1, 1, 2, 3, 3, 3, 2][j]; for (let i = -hw; i <= hw; i++) p.set(18 + i, deck - 10 + j, i === -hw ? '#b8763e' : i === hw ? '#5a3218' : '#8a5228'); }
  for (let y = deck - 19; y < deck - 10; y++) p.set(18, y, '#3a2416');
  p.set(15 + f, deck - 6 - f, '#e8b890');
  // drums
  fig(26, '#2a3a5a', '#6a4a2a', 4, null);
  for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) { const d = Math.hypot(i, j); if (d < 3.6) p.set(34 + i, deck - 4 + j, d > 2.5 ? '#c8323a' : '#f4f0e8'); }
  for (let x = 30; x <= 33; x++) p.set(x, deck - 9, '#d8d8e0');
  for (let y = deck - 11 + f; y < deck; y++) p.set(39, y, '#6a6a78');
  for (let x = 37; x <= 41; x++) p.set(x, deck - 12 + f, x === 39 ? G : GD);
  p.line(29, deck - 10, 32, deck - 11 - f * 2, '#e8dcc0'); p.line(30, deck - 9, 36, deck - 11 + f * 2, '#e8dcc0');
  // trumpet
  fig(46, '#c8423a', '#1a1418', f, '#e8dcc0');
  p.line(49, deck - 12 - f, 54, deck - 12 - f * 3, G); p.set(55, deck - 13 - f * 3, G); p.set(55, deck - 11 - f * 3, GD);
  // saxophone
  fig(57, '#1e2230', '#d8b46a', 1 - f);
  p.line(60, deck - 11 + f, 61, deck - 6 + f, G); p.set(62, deck - 5 + f, G); p.set(63, deck - 6 + f, GD); p.set(63, deck - 7 + f, G);
  // banjo
  fig(67, '#3a8a5a', '#8a3a22', 0, '#c8a24a');
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) p.set(70 + i, deck - 6 + j, i || j ? '#e8e0d0' : '#f8f4ec');
  p.line(71, deck - 7, 75, deck - 10, '#6a4a2a'); p.set(69 + f, deck - 6, '#e8b890');
  // two dancing in the bow
  fig(77 - f, '#d86a8a', '#e8d08a', f); fig(81 - f, '#2f5fa8', '#2a1a14', f);
  return p.done();
}
/** a kayak (bow to the left), paddle frame f of 4 */
function renderKayak(f, hull, vest) {
  const p = pix(26, 13);
  for (let x = 2; x <= 23; x++) p.set(x, 10, mixHex(hull, '#ffffff', 0.25));
  for (let x = 0; x <= 25; x++) p.set(x, 11, hull);
  for (let x = 3; x <= 22; x++) p.set(x, 12, mixHex(hull, '#0a0e22', 0.45));
  for (let y = 5; y <= 9; y++) for (let x = 11; x <= 14; x++) p.set(x, y, x === 11 ? mixHex(vest, '#ffffff', 0.2) : x === 14 ? mixHex(vest, '#0a0c1c', 0.4) : vest);
  p.set(12, 2, '#3a2a1c'); p.set(13, 2, '#3a2a1c'); p.set(11, 3, '#e8b890'); p.set(12, 3, '#e8b890'); p.set(13, 3, '#3a2a1c'); p.set(12, 4, '#d8a078');
  const [ax, ay, bx, by] = [[5, 11, 18, 3], [5, 6, 19, 6], [7, 3, 19, 10], [5, 6, 19, 6]][f];
  p.line(ax, ay, bx, by, '#2a2c3a');
  for (const [x, y] of [[ax, ay], [bx, by]]) { p.set(x, y, '#f4f0e8'); p.set(x + (x < 12 ? -1 : 1), y, '#f4f0e8'); p.set(x, y + (y > 6 ? 1 : -1), '#d8d6d0'); }
  p.set(10, 7 + (f & 1), '#e8b890'); p.set(15, 7 - (f & 1), '#e8b890');
  return p.done();
}
/** the picnic boat (bow to the right): friends round a table with a lantern, one at the tiller */
const PICNIC_W = 38, PICNIC_H = 18;
// the one at the tiller and the two at the table (they say skål) always sail; the two friends beside them thin
const PICNIC_RANK = { 9: 0.35, 27: 0.7 };
const picnicLevel = crowd => (PICNIC_RANK[9] < crowd ? 1 : 0) + (PICNIC_RANK[27] < crowd ? 1 : 0);
/** n: how many of the two extra friends are aboard (2 = everyone); party: false leaves only the one at the tiller (crowd 0) */
function renderPicnic(n = 2, party = true) {
  const p = pix(PICNIC_W, PICNIC_H), r = rng(606);
  for (let y = 11; y < 18; y++) for (let x = 0; x < PICNIC_W; x++) {
    if (PICNIC_W - 1 - x < (y - 11) * 1.4 || x < (y > 15 ? 1 : 0)) continue;
    p.set(x, y, y === 11 ? '#f8f4ec' : y === 13 ? '#2a9a94' : y >= 16 ? '#1a3a4a' : '#e4e0d8');
  }
  for (const x of [3, 9, 14, 22, 27]) {
    const coat = COATS[Math.floor(r() * COATS.length)], hair = HAIRS[Math.floor(r() * HAIRS.length)];
    if ((x === 27 && n < 2) || (x === 9 && n < 1) || (!party && x !== 3)) continue;
    p.set(x, 4, hair); p.set(x + 1, 4, hair); p.set(x, 5, hair); p.set(x + 1, 5, '#e8b890');
    for (let y = 6; y < 11; y++) for (let xx = x - 1; xx <= x + 2; xx++) p.set(xx, y, xx === x + 2 ? mixHex(coat, '#ffd090', 0.3) : coat);
  }
  for (let x = 16; x <= 20; x++) p.set(x, 9, '#c8a878');
  p.set(18, 10, '#5a4634'); p.set(18, 8, '#ffe8a0'); p.set(18, 7, '#fff8e0'); p.set(18, 6, '#3a3026'); p.set(16, 8, '#3a8a4a'); p.set(20, 8, '#e8e0c8');
  return p.done();
}
const SWAN_KEY = { w: '#f4f6fa', s: '#c2cce0', o: '#f08a2a', k: '#1a1a22', g: '#a4a6a4', d: '#7e8088' };
const SWAN = ['......ww.', '......wok', '......w..', '.....ws..', '.ww..ws..', 'wwwwwws..', 'wwwwwwss.', '.sssss...'];
const CYGNET = ['..gg', 'ggg.', 'ddd.'];
// the swan family's rounds near our quay: two adults, four cygnets in a line behind (phase lag)
const SWANS = [{ lag: 0, big: true }, { lag: 0.022, big: false }, { lag: 0.034, big: false }, { lag: 0.046, big: false }, { lag: 0.058, big: false }, { lag: 0.08, big: true }];
// (fed: false when nobody is there to throw them bread: they keep to their rounds)
function swanAt(lag, t, fed = true) {
  const a = TAU * (2 * (t / LOOP - lag) + 0.08), feed = fed ? smooth(FEED.t0, FEED.t0 + 8, t) * (1 - smooth(FEED.t1 - 6, FEED.t1, t)) : 0;
  const x = 214 + 112 * Math.sin(a) + 14 * Math.sin(a * 3 + 1), y = 198 + 3.5 * Math.sin(TAU * (3 * (t / LOOP - lag) + 0.3));
  return { x: lerp(x, 268 + lag * 420, feed), y: lerp(y, 202 - lag * 40, feed), vx: feed > 0.6 ? 1 : Math.cos(a) + 0.375 * Math.cos(a * 3 + 1) };
}

/* ================= The canal ================= */
function renderWater() {
  const h = NEDGE - WL, p = pix(W, h);
  const P = ['#1c2a52', '#243868', '#2e487e', '#3a5a92', '#4a6ea6', '#5c82b4'];
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const f = y / h, v = 0.18 + 0.62 * Math.sin(Math.PI * Math.pow(f, 0.8)) * (0.8 + 0.2 * vnoise(x / 50, y / 4, 9)) - (f > 0.86 ? (f - 0.86) * 3 : 0);
    p.set(x, y, pick(P, v, x, y, 0.5));
  }
  return p.done();
}
/** the far side as the canal mirrors it: a still of the houses, the quay and the ships, upside down, squashed and softened */
// (quay, base: a thinner level of the terraces to mirror, and the full mirror to take every row but the diners' from)
function renderMirror(quay = QUAY_C, base = null) {
  const [, x] = makeCanvas(W, WL);
  x.drawImage(SKY_C, 0, 0); x.drawImage(ROW_C, 0, 0);
  for (const w of WINS) if (w.mode !== 'off') { x.fillStyle = w.c1; x.fillRect(w.x, w.y, w.w, w.h); }
  x.drawImage(quay.c, quay.x, quay.y);
  for (const S of SHIPS) { x.drawImage(S.hullC.c, S.hullC.x, S.hullC.y); x.drawImage(S.rig.c, S.rig.x, S.rig.y); }
  const src = x.getImageData(0, 0, W, WL).data, rows = NEDGE - WL, p = pix(W, rows);
  if (base) { p.x.drawImage(base, 0, 0); p.grab(); }
  for (let k = base ? DINER_K0 : 0; k <= (base ? DINER_K1 : rows - 1); k++) for (let i = 0; i < W; i++) {
    const sy = WL - 1 - k * 2.3, acc = [0, 0, 0];
    let n = 0;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -1; dx <= 1; dx++) {
      const yy = Math.round(sy + dy), xx = clamp(i + dx, 0, W - 1);
      if (yy < 0 || yy >= WL) continue;
      const o = (yy * W + xx) * 4;
      acc[0] += src[o]; acc[1] += src[o + 1]; acc[2] += src[o + 2]; n++;
    }
    p.set(i, k, acc.map(v => Math.min(255, Math.floor(v / n / 20 + bayer(i, k)) * 20)));
  }
  return p.done();
}
// the mirror's rows that can hold the terraces' diners (row k is a blur of the rows WL - 1 - k * 2.3 +- 2.5)
const DINER_K0 = Math.ceil((WL - 1 - (DINER_Y + DINER_H - 1) - 2.5) / 2.3), DINER_K1 = Math.floor((WL - 1 - DINER_Y + 2.5) / 2.3);
const GLINTS = [];                                  // drifting glints: the sky's light and its peach edge on the ripples
{
  const r = rng(909);
  for (let i = 0; i < 90; i++) GLINTS.push({ y: WL + 2 + Math.floor(r() * (NEDGE - WL - 4)), x: r(), k: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 4)),
    len: 2 + Math.floor(r() * 7), kf: 18 + Math.floor(r() * 40), ph: r(), c: r() < 0.35 ? '#f6c8a4' : r() < 0.8 ? '#c4d6f4' : '#182448' });
}

/* ================= Our quay (the south side): granite coping, cobbles, the cycle lane, lanterns, parked bikes ================= */
const BOLLARDS = [70, 212, 264, 362, 419];          // iron mooring bollards on the coping
const COBBLE = ['#2c3350', '#394262', '#485274', '#586284'], WARMCOB = ['#5a4a4c', '#7c6454', '#9e7c5a', '#c0985e'];
/** warm light on our quay: the two lanterns and the hot dog wagon's hatch */
function nearGlow(x, y) {
  let s = 0;
  for (const l of NEAR_LAMPS) s += Math.exp(-Math.hypot((x - l.x) / 62, (y - 222) / 20));
  s += 0.9 * Math.exp(-Math.hypot((x - (CART.x + CART.w + 6)) / 40, (y - 232) / 16));
  return Math.min(1, s);
}
function renderNearQuay() {
  const p = pix(W, H);
  let cy = NEDGE + 3, ch = 3, course = 0;
  const rows = [];                                                                  // cobble courses grow towards us
  while (cy < H) { rows.push([cy, ch, course++]); cy += ch; ch = Math.min(8, ch + (course % 2 ? 0 : 1)); }
  for (let y = NEDGE; y < H; y++) for (let x = 0; x < W; x++) {
    const g = nearGlow(x, y);
    let col;
    if (y === NEDGE) col = pick(['#8a90aa', '#a8aabc', '#d0c2a8'], 0.3 + g * 0.7, x, y);
    else if (y === NEDGE + 1) col = pick(['#5c627e', '#74768c', '#9a8c7c'], 0.3 + g * 0.7, x, y);
    else if (y === NEDGE + 2) col = '#262b44';
    else {
      const [y0, hh, c] = rows.find(q => y < q[0] + q[1]) || rows[rows.length - 1], sw = 5 + hh * 2;
      const bike = y >= LANE_BIKE - 8 && y <= LANE_BIKE + 5;
      const joint = y === y0 || (x + c * 7 + (c & 1) * (sw >> 1)) % sw === 0;
      const n = hash(Math.floor((x + c * 7 + (c & 1) * (sw >> 1)) / sw), c, 4);
      let v = bike ? 0.34 + (hash(x, y, 8) - 0.5) * 0.12 : joint ? 0.08 : 0.3 + n * 0.5;
      if (bike && (y === LANE_BIKE - 8 || y === LANE_BIKE + 5)) v = 0.85;                              // the lane's kerb lines
      col = g + (bayer(x, y) - 0.5) * 0.16 > 0.45 ? pick(WARMCOB, v + g * 0.3, x, y, 0.35) : pick(COBBLE, v + g * 0.5, x, y, 0.35);
    }
    p.set(x, y, col);
  }
  for (const bx of BOLLARDS) for (let j = 0; j < 7; j++) for (let i = -2; i <= 2; i++) {            // bollards
    if ((j === 0 || j === 6) && Math.abs(i) === 2 && j === 0) continue;
    if (j > 1 && j < 5 && Math.abs(i) === 2) continue;
    p.set(bx + i, NEDGE - 5 + j, i === -2 || (i === -1 && j > 1) ? '#5a6488' : j === 0 ? '#3a4262' : '#161a2c');
  }
  // a bicycle painted on the cycle lane, foreshortened: wheels, the frame, saddle and handlebar
  const SYM = ['................111.......', '........1111.....1........', '.........1.......1........', '.........111111111........',
    '..11111...1.....11.11111..', '.1.....1...1.111..11....1.', '1...111111111....1..11...1', '.1.....1..........1.....1.', '..11111............11111..'];
  SYM.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(250 + i, LANE_BIKE - 5 + j, '#b8bed2'); });
  // parked bikes along the edge on the right
  const BIKE = ['...11......1...', '....1.....11...', '..111111111....', '.1.1..1...1.1..', '1...11.....1..1', '1..1.1....1...1', '.11...1....111.'];
  for (const [bx, col] of [[424, '#1a1e30'], [437, '#5a1c20'], [461, '#1c3a2e']])
    BIKE.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(bx + i, NEDGE + 4 + j, j < 2 && i > 9 ? '#8a8ea0' : j > 2 && (i < 4 || i > 10) ? '#10121c' : col); });
  return trim(p);
}
function renderNearLamps() {
  const p = pix(W, H);
  for (const l of NEAR_LAMPS) {
    for (let y = l.y + 5; y <= NEDGE + 5; y++) { p.set(l.x, y, '#2a3450'); p.set(l.x + 1, y, '#0e1220'); }
    for (let i = -2; i <= 3; i++) { p.set(l.x + i, NEDGE + 5, '#161a2c'); if (i > -2 && i < 3) p.set(l.x + i, NEDGE + 3, '#1c2238'); }
    for (let i = -2; i <= 3; i++) p.set(l.x + i, l.y + 4, '#161a2c');
    for (let y = l.y - 3; y <= l.y + 3; y++) for (let i = -2; i <= 3; i++) {
      const edge = i === -2 || i === 3;
      p.set(l.x + i, y, edge ? '#1c2238' : y < l.y - 1 ? '#fff8e0' : y < l.y + 2 ? '#ffe9b0' : '#f6c06c');
    }
    for (let i = -3; i <= 4; i++) p.set(l.x + i, l.y - 4, '#161a2c');
    for (let i = -1; i <= 2; i++) p.set(l.x + i, l.y - 5, '#1c2238');
    p.set(l.x, l.y - 6, '#2a3450'); p.set(l.x + 1, l.y - 6, '#161a2c');
  }
  return trim(p);
}

/* ================= The bow of one of the museum's old wooden ships, moored at our quay (right edge of the frame) ================= */
// (the red lightship No. XVII that used to lie here was towed to Helsingor in 2018, so she is not drawn)
const LS = { x: 428, top: 183 };                     // her stem at the rail, the rail's row
function renderMuseumShip() {
  const p = pix(W, H), TAR = ['#14261f', '#1c382c', '#274c3a', '#35624a', '#4a7a5a', '#6a8e62'];
  for (let y = LS.top; y < NEDGE + 2; y++) for (let x = LS.x + Math.round((y - LS.top) * 0.36); x < W; x++) {
    const j = y - LS.top, lit = Math.exp(-Math.hypot((x - 420) / 40, (y - 188) / 22));
    let col = pick(TAR, 0.34 + 0.6 * lit - j * 0.008 + (hash(x >> 2, y, 3) - 0.5) * 0.1, x, y, 0.5);
    if (j < 2) col = j ? '#6a4c30' : '#b08a58'; else if (j === 3) col = '#d8b450'; else if (j === 9) col = '#0c1612';   // varnished rail, yellow strake, wale
    else if ((j === 6 || j === 13 || j === 18) && hash(x >> 3, j, 5) < 0.8) col = '#10201a';             // plank seams
    p.set(x, y, col);
  }
  for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1], [0, 2], [-1, 3], [0, 3], [1, 3], [2, 3], [-2, 4], [3, 4]]) p.set(441 + dx, 196 + dy, dy < 2 ? '#05060a' : '#4a4e62');   // hawse pipe, anchor
  p.line(LS.x + 2, LS.top - 1, LS.x + 2, LS.top - 15, '#c8ccd8');                                     // jackstaff
  for (let y = LS.top - 6; y < LS.top; y++) for (let x = 444; x < 453; x++) p.set(x, y, y === LS.top - 6 ? '#55596c' : x === 444 ? '#3a3e52' : '#1e2234');   // windlass
  for (let y = LS.top - 9; y < LS.top; y++) for (let x = 462; x < W; x++) p.set(x, y, y === LS.top - 9 ? '#c8b89a' : (y === LS.top - 6 && x % 6 < 3) ? '#ffd88a' : x === 462 ? '#8a7a66' : '#6a5a4c');   // deckhouse
  p.line(LS.x + 4, LS.top - 1, W + 52, 96, '#232846');                                                // the forestay, up to her mast (out of frame), clear of HAVN
  p.line(LS.x + 6, 191, 420, NEDGE - 2, '#b8a888'); p.line(LS.x + 12, 192, 436, NEDGE - 1, '#8a7a62');   // mooring lines
  return trim(p);
}

/* ================= The hot dog wagon: white, a striped canopy, a lit hatch, sauces on the shelf ================= */
function renderCart() {
  const p = pix(W, H), { x, y, w, h } = CART;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const X = x + i, Y = y + j;
    let col = pick(['#b8bccc', '#d6d6dc', '#f0ece2'], 0.45 + 0.4 * (i / w) - j * 0.006, X, Y, 0.5);
    if (j < 2) col = '#8a2a30'; else if (i === 0) col = '#8a8ea4'; else if (j === h - 1) col = '#5a5e74';
    if (j >= 4 && j < 10 && i > 2 && i < w - 3) col = '#b8282e';                                        // the name band
    if (j >= 12 && j < 25 && i > 9 && i < w - 4) col = j < 14 ? '#ffe9b0' : j < 22 ? '#f8c474' : '#8a6a48';   // the hatch, its counter
    if (j === 25 && i > 7 && i < w - 2) col = '#c8ccd8';                                               // the shelf
    if (j >= 29 && (i % 9 === 4)) col = '#c8ccd8';
    p.set(X, Y, col);
  }
  for (let i = -3; i < w + 5; i++) for (let j = 0; j < 4; j++)                                         // the canopy over the hatch side
    p.set(x + i, y - 4 + j, j === 3 ? ((i >> 2) & 1 ? '#f4f0e8' : '#c8323a') : (Math.floor((i + j) / 4) & 1) ? '#e8e2d6' : '#b8282e');
  for (const [i, col] of [[13, '#d8303a'], [16, '#f0c030'], [19, '#e8dcc0'], [36, '#3a8a4a']]) { p.set(x + i, y + 23, col); p.set(x + i, y + 24, col); p.set(x + i, y + 22, '#f4f0e8'); }
  for (const wx of [x + 8, x + w - 10]) for (let j = -3; j <= 3; j++) for (let i = -3; i <= 3; i++) {   // wheels
    const d = Math.hypot(i, j);
    if (d < 3.6) p.set(wx + i, y + h + j, d > 2.4 ? '#10121c' : d > 1.2 ? '#6a6e84' : '#c8ccd8');
  }
  for (let i = 0; i < 8; i++) p.set(x + w + i, y + h - 3, '#5a5e74');                                   // the tow bar
  p.set(x + w + 7, y + h - 2, '#5a5e74'); p.set(x + w + 7, y + h - 1, '#5a5e74');
  for (let j = 0; j < 7; j++) { p.set(x + 6, y - 11 + j, '#8a8ea4'); p.set(x + 7, y - 11 + j, '#5a5e74'); }   // the stove pipe
  const c = trim(p);
  const cx = c.c.getContext('2d');
  sign(cx, 'PØLSER', x + 11 - c.x, y + 5 - c.y, '#fff4dc');
  return c;
}

/* ================= Soap bubbles (the city's own drifting thing, three depths) ================= */
function makeBubble(r) {
  const s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    const d = Math.hypot(x - r, y - r);
    if (d > r + 0.4) continue;
    if (d > r - 0.75) p.set(x, y, x + y < s - 1 ? '#e8f4ff' : x > y ? '#f6b8e0' : '#9ae0e8', 215);
    else if (r > 2) p.set(x, y, '#cfe4ff', 34);
  }
  if (r > 1) p.set(r - Math.ceil(r / 2), r - Math.ceil(r / 2), '#ffffff', 250);
  return p.done();
}
const BUBBLES = [[], [], []];                       // far: high over the roofs; mid: over the canal; near: fresh from the wand
{
  const r = rng(2323);
  [[26, 5, 8], [18, 8, 12], [11, 12, 18]].forEach(([count, nMin, nMax], layer) => {
    for (let i = 0; i < count; i++) BUBBLES[layer].push({ id: layer * 100 + i, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 10 + Math.floor(r() * 20), p2: r(), size: r() });
  });
}

/* ================= People on our quay (prerendered sprites, facing right; flipped copies face left) ================= */
const PW = 17, PT = 4;                               // sprite width, headroom above the head (hats, raised arms)
/** P: { h, coat, legs, hair, skin, hat, pack, tote, dress, shorts, sleeve, long }; f: 0..3 walk cycle, 4 standing; pose for f = 4 */
function makePerson(P, f, pose) {
  const h = P.h, p = pix(PW, h + PT), cx = 7;
  const legL = Math.round(h * 0.42), headH = Math.max(4, Math.round(h * 0.2)), torsoL = h - legL - headH;
  const dip = f === 0 || f === 2 ? 1 : 0, yHead = PT + dip, yTorso = yHead + headH, yHip = yTorso + torsoL, yFoot = PT + h - 1;
  const sp = f === 0 ? 1 : f === 2 ? -1 : 0, reach = Math.max(2, Math.round(h / 7));
  const legsD = mixHex(P.legs, '#05060c', 0.42), coatD = mixHex(P.coat, '#0a0c1c', 0.4), coatL = mixHex(P.coat, '#ffe2b0', 0.25), skinD = mixHex(P.skin, '#4a2a30', 0.3);
  const leg = (dx, col, lift) => {
    const L = yFoot - yHip - lift;
    for (let j = 0; j <= L; j++) {
      const x = cx + Math.round(dx * j / L), bare = (P.shorts || P.dress) && j > L * 0.4, shoe = j === L;
      p.set(x, yHip + j, shoe ? '#141018' : bare ? P.skin : col); p.set(x + 1, yHip + j, shoe ? '#141018' : bare ? skinD : col);
    }
    p.set(cx + dx + 2, yHip + L, '#141018');
  };
  leg(-sp * reach, legsD, f === 1 ? 1 : 0);
  leg(sp * reach, P.legs, f === 3 ? 1 : 0);
  const hem = yHip + (P.dress ? Math.round(legL * 0.42) : P.long ? 3 : 1);
  for (let y = yTorso; y <= hem; y++) {
    const fl = P.dress && y > yHip ? Math.round((y - yHip) * 0.45) : 0, sh = y === yTorso ? 1 : 0;
    for (let x = cx - 2 - fl + sh; x <= cx + 3 + fl - sh; x++) p.set(x, y, x === cx - 2 - fl + sh ? coatD : x === cx + 3 + fl - sh ? coatL : P.coat);
  }
  if (P.stripe) for (let y = yTorso + 2; y < yHip; y += 2) for (let x = cx - 1; x <= cx + 2; x++) p.set(x, y, P.stripe);
  // head: hair behind, the face in front
  for (let y = yHead; y < yHead + headH - 1; y++) for (let x = cx - 1; x <= cx + 2; x++) p.set(x, y, y > yHead && x > cx ? P.skin : P.hair);
  p.set(cx + 2, yHead + 1, P.skin); p.set(cx, yHead + headH - 1, skinD); p.set(cx + 1, yHead + headH - 1, P.skin);
  if (P.longHair) for (let y = yHead + 1; y < yTorso + 4; y++) p.set(cx - 2 + (y > yTorso + 1 ? 1 : 0), y, P.hair);
  if (P.hat) { for (let x = cx - 2; x <= cx + 3; x++) p.set(x, yHead, P.hat); for (let x = cx - 1; x <= cx + 2; x++) p.set(x, yHead - 1, P.hat); }
  if (P.cap) { for (let x = cx - 1; x <= cx + 3; x++) p.set(x, yHead, P.cap); p.set(cx, yHead - 1, P.cap); p.set(cx + 1, yHead - 1, P.cap); }
  if (P.pack) for (let y = yTorso + 1; y < yTorso + 7; y++) { p.set(cx - 3, y, P.pack); p.set(cx - 4, y, mixHex(P.pack, '#000000', 0.35)); }
  // arms
  const sh = yTorso + 1, armL = torsoL - 1, sleeve = j => (P.sleeve && j > armL * 0.4 ? P.skin : coatD);
  const arm = (x1, y1, hand = P.skin) => { p.line(cx + 1, sh, x1, y1, coatD); p.set(x1, y1, hand); };
  if (pose === 'camera') { arm(cx + 5, yHead + 2); p.fill(cx + 5, yHead + 1, 3, 2, '#1a1c28'); p.set(cx + 7, yHead + 1, '#8a8ea0'); }
  else if (pose === 'wave') arm(cx + 4, yHead - 2);
  else if (pose === 'cheer') { arm(cx + 4, yHead - 3); p.line(cx, sh, cx - 3, yHead - 3, coatD); p.set(cx - 3, yHead - 3, P.skin); }
  else if (pose === 'eat') { arm(cx + 3, yHead + 3); p.set(cx + 4, yHead + 3, '#e8b060'); p.set(cx + 4, yHead + 2, '#c8423a'); }
  else if (pose === 'point') arm(cx + 6, sh + 1);
  else if (pose === 'up') arm(cx + 5, yHead);
  else {
    const sw = -sp * 2;
    for (let j = 0; j <= armL; j++) p.set(cx + 1 + Math.round(sw * j / armL), sh + j, j === armL ? P.skin : sleeve(j));
    if (P.tote) { const hx = cx + 1 + sw, by = sh + armL + 1; p.fill(hx - 1, by, 4, 5, P.tote); for (let j = 0; j < 5; j++) p.set(hx + 2, by + j, mixHex(P.tote, '#000000', 0.3)); }
    if (P.cone) { p.set(cx + 4, sh + 2, '#f8e8f0'); p.set(cx + 4, sh + 1, '#f4a8c0'); p.set(cx + 4, sh + 3, '#c89848'); p.set(cx + 3, sh + 3, P.skin); }
  }
  return p.done();
}
const STYLES = [
  { h: 24, coat: '#c8423a', legs: '#2a3458', hair: '#2a1a14', skin: '#f0c4a0', tote: '#e8dcc0' },
  { h: 23, coat: '#e8e0d0', legs: '#3a5a8a', hair: '#d8b46a', skin: '#f4d0b0', longHair: true, dress: true },
  { h: 25, coat: '#2a3a5a', legs: '#1e2230', hair: '#1a1418', skin: '#d8a078', pack: '#c8782a', long: true },
  { h: 24, coat: '#e8c040', legs: '#2a2e3e', hair: '#6a4a2a', skin: '#e8b890', cap: '#2f5fa8', shorts: true },
  { h: 22, coat: '#3a8a5a', legs: '#e8e0d0', hair: '#8a3a22', skin: '#f0c4a0', longHair: true, cone: true },
  { h: 25, coat: '#8a8e9e', legs: '#262a3a', hair: '#c8c4c0', skin: '#e8b890', hat: '#3a3026', long: true },
  { h: 23, coat: '#d86a8a', legs: '#2a3458', hair: '#1a1418', skin: '#b07850', longHair: true, tote: '#2f5fa8' },
  { h: 24, coat: '#5a9ab8', legs: '#3a3a44', hair: '#4a3020', skin: '#f0c4a0', sleeve: true, stripe: '#f4f0e8' },
  { h: 23, coat: '#e87a3a', legs: '#1e2230', hair: '#e8d08a', skin: '#f4d0b0', longHair: true, dress: true },
  { h: 26, coat: '#1e2230', legs: '#3a5a8a', hair: '#2a1a14', skin: '#8a5a3c', pack: '#3a8a5a' },
  { h: 24, coat: '#7a4a8a', legs: '#262a3a', hair: '#6a4a2a', skin: '#e8b890', tote: '#e8c040', long: true },
  { h: 22, coat: '#f4f0e8', legs: '#c8423a', hair: '#1a1418', skin: '#d8a078', sleeve: true, shorts: true, cap: '#e8e0d0' },
  { h: 25, coat: '#2f5fa8', legs: '#e8dcc0', hair: '#8a3a22', skin: '#f0c4a0', sleeve: true, cone: true },
  { h: 24, coat: '#4a6a3a', legs: '#2a2420', hair: '#d8b46a', skin: '#f4d0b0', longHair: true, pack: '#8a2a34' },
  { h: 15, coat: '#e8c040', legs: '#2f5fa8', hair: '#d8b46a', skin: '#f4d0b0', shorts: true },            // 14, 15: children
  { h: 14, coat: '#d8303a', legs: '#2a3458', hair: '#6a4a2a', skin: '#f0c4a0', dress: true, longHair: true },
  { h: 26, coat: '#3a3e52', legs: '#1e2230', hair: '#1a1418', skin: '#e8b890', long: true, tote: '#c8a24a' },
  { h: 26, coat: '#b8323a', legs: '#2a2e3e', hair: '#e8d08a', skin: '#f4d0b0', longHair: true, long: true },
];
// walk[style][frame] = [facing right, facing left]; stand[style][pose] likewise
let WALK, STAND;
const POSES = ['idle', 'camera', 'wave', 'cheer', 'eat', 'point', 'up'];
function buildPeople() {
  const both = c => [c, flipC(c)];
  WALK = STYLES.map(P => [0, 1, 2, 3].map(f => both(makePerson(P, f))));
  STAND = STYLES.map(P => Object.fromEntries(POSES.map(pose => [pose, both(makePerson(P, 4, pose === 'idle' ? null : pose))])));
}
// the stream of strollers: [style, crossings per loop, phase, direction, feet row, unit]; a pair shares k and has a small
// phase gap. unit: who walks together (a pair, a parent and child; the man in the hat and his dog are one entry);
// it is turned into the unit's crowd rank right below
const STROLL = [
  [0, 5, 0.02, 1, 222, 0], [1, 5, 0.045, 1, 223, 0], [2, 6, 0.3, -1, 225, 1], [3, 7, 0.52, 1, 221, 2], [4, 4, 0.71, -1, 224, 3], [14, 4, 0.74, -1, 226, 3],
  [5, 4, 0.12, 1, 226, 4], [6, 6, 0.86, -1, 222, 5], [7, 6, 0.89, -1, 223, 5], [8, 5, 0.4, 1, 225, 6], [9, 7, 0.18, -1, 227, 7], [10, 5, 0.63, -1, 221, 8],
  [11, 6, 0.57, 1, 227, 9], [12, 4, 0.33, 1, 222, 10], [15, 4, 0.355, 1, 224, 10], [13, 7, 0.94, 1, 224, 11],
  [16, 5, 0.22, -1, 256, 12], [17, 4, 0.78, 1, 258, 13], [2, 6, 0.55, 1, 255, 14], [6, 5, 0.97, -1, 259, 15], [9, 4, 0.08, 1, 257, 16],
];
for (const q of STROLL) q[5] = rankOf(q[5]);
const SPAN = W + 44;                                  // a stroller's whole walk: just outside the frame on either side

/* ================= People sitting on the coping, backs to us, legs over the water ================= */
const SW = 17, SHT = 17, SCX = 8;                    // sprite size; the seat is its bottom row
function makeSitter(S, pose) {
  const p = pix(SW, SHT), cx = SCX, coatD = mixHex(S.coat, '#0a0c1c', 0.4), coatL = mixHex(S.coat, '#ffe2b0', 0.2);
  const lean = pose === 'leanL' ? -1 : pose === 'leanR' ? 1 : 0;
  for (let y = 15; y <= 16; y++) for (let x = cx - 4; x <= cx + 3; x++) p.set(x, y, x === cx - 4 ? mixHex(S.legs, '#05060c', 0.4) : S.legs);
  for (let y = 8; y <= 14; y++) for (let x = cx - 3; x <= cx + 2; x++) p.set(x + (y < 11 ? lean : 0), y, x === cx - 3 ? coatD : x === cx + 2 ? coatL : y === 8 && S.hood ? S.hood : S.coat);
  for (let x = cx - 4; x <= cx + 3; x++) p.set(x + lean, 8, x < cx - 2 ? coatD : S.coat);
  p.set(cx - 1 + lean, 7, S.skin); p.set(cx + lean, 7, S.skin);
  for (let y = 3; y <= 6; y++) for (let x = cx - 2; x <= cx + 1; x++) p.set(x + lean, y, S.hair);
  if (S.longHair) for (let y = 7; y <= 10; y++) for (let x = cx - 2; x <= cx + 1; x++) p.set(x + lean, y, S.hair);
  if (S.hat) for (let x = cx - 3; x <= cx + 2; x++) { p.set(x + lean, 3, S.hat); if (x > cx - 3 && x < cx + 2) p.set(x + lean, 2, S.hat); }
  if (pose === 'turnL') { p.set(cx - 2, 5, S.skin); p.set(cx - 2, 4, S.skin); p.set(cx - 3, 5, S.skin); }
  if (pose === 'turnR') { p.set(cx + 1, 5, S.skin); p.set(cx + 1, 4, S.skin); p.set(cx + 2, 5, S.skin); }
  // arms: resting on the coping, or doing something
  const rest = side => { for (let y = 9; y <= 14; y++) p.set(cx + (side < 0 ? -4 : 3), y, coatD); p.set(cx + (side < 0 ? -5 : 4), 15, S.skin); };
  if (pose === 'sip') { rest(-1); p.line(cx + 3, 9, cx + 4, 12, coatD); p.line(cx + 4, 12, cx + 2, 7, coatD); p.set(cx + 2, 6, S.skin); p.set(cx + 3, 5, '#3a8a4a'); p.set(cx + 3, 4, '#3a8a4a'); p.set(cx + 3, 3, '#9ad0a8'); }
  else if (pose === 'cheer' || pose === 'cheer2') {
    const up = pose === 'cheer' ? 0 : 1;
    p.line(cx - 4, 9, cx - 6 + up, 2 + up, coatD); p.set(cx - 6 + up, 1 + up, S.skin); p.line(cx + 3, 9, cx + 5 - up, 2 + up, coatD); p.set(cx + 5 - up, 1 + up, S.skin);
  } else if (pose === 'pointL') { rest(1); p.line(cx - 4, 9, cx - 8, 7, coatD); p.set(cx - 8, 6, S.skin); }
  else if (pose === 'pointR') { rest(-1); p.line(cx + 3, 9, cx + 7, 7, coatD); p.set(cx + 8, 6, S.skin); }
  else if (pose === 'clink') { rest(-1); p.line(cx + 3, 9, cx + 6, 6, coatD); p.set(cx + 7, 5, '#3a8a4a'); p.set(cx + 7, 4, '#3a8a4a'); p.set(cx + 7, 3, '#9ad0a8'); }
  else { rest(-1); rest(1); }
  return p.done();
}
const SIT_POSES = ['idle', 'turnL', 'turnR', 'sip', 'cheer', 'cheer2', 'pointL', 'pointR', 'clink', 'leanL', 'leanR'];
// who sits where: x of the sprite's centre, the group (friends toast together) and the crowd rank. A group thins down
// to a pair (nobody is left to clink alone), then the pair leaves together; the two friends by the guitarist stay
// (rank 0: she is the one who throws the swans their bread)
const SITTERS = [
  { x: 90, g: 0, rank: 0.62, coat: '#c8423a', legs: '#2a3458', hair: '#d8b46a', skin: '#f4d0b0', longHair: true },
  { x: 101, g: 0, rank: 0.62, coat: '#2a3a5a', legs: '#1e2230', hair: '#2a1a14', skin: '#e8b890', hood: '#3a4a6e' },
  { x: 146, g: 1, rank: 0.9, coat: '#e8c040', legs: '#3a5a8a', hair: '#1a1418', skin: '#d8a078' },
  { x: 157, g: 1, rank: 0.2, coat: '#f4f0e8', legs: '#2a3458', hair: '#8a3a22', skin: '#f0c4a0', longHair: true },
  { x: 168, g: 1, rank: 0.2, coat: '#3a8a5a', legs: '#262a3a', hair: '#6a4a2a', skin: '#e8b890', hat: '#c8a24a' },
  { x: 228, g: 2, rank: 0, coat: '#5a9ab8', legs: '#2a2e3e', hair: '#e8d08a', skin: '#f4d0b0', longHair: true },
  { x: 239, g: 2, rank: 0, coat: '#7a4a8a', legs: '#1e2230', hair: '#1a1418', skin: '#b07850' },
  { x: 312, g: 3, rank: 0.5, coat: '#e87a3a', legs: '#3a5a8a', hair: '#4a3020', skin: '#f0c4a0' },
  { x: 323, g: 3, rank: 0.5, coat: '#d8d0c0', legs: '#2a3458', hair: '#2a1a14', skin: '#e8b890', longHair: true },
  { x: 376, g: 4, rank: 0.35, coat: '#2f5fa8', legs: '#262a3a', hair: '#c8c4c0', skin: '#e8b890', hat: '#1e2230' },
  { x: 387, g: 4, rank: 0.35, coat: '#d86a8a', legs: '#2a3458', hair: '#6a4a2a', skin: '#f4d0b0', longHair: true },
  { x: 398, g: 4, rank: 0.75, coat: '#8a8e9e', legs: '#1e2230', hair: '#d8b46a', skin: '#f0c4a0' },
];
let SIT_SPR;

/* ================= Cyclists (facing right, wheels on the last row; f: quarter turns of the cranks) ================= */
const CH = 31, WY = 25;                              // sprite height, wheel hub row
function makeCyclist(P, f) {
  const cargo = !!P.cargo, p = pix(cargo ? 46 : 30, CH), fw = cargo ? 36 : 23, T = '#12141e';
  const wheel = (cx, r) => {
    for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) { const d = Math.hypot(i, j); if (d <= r + 0.4 && d > r - 0.9) p.set(cx + i, WY + 5 - r + j, T); }
    const cy = WY + 5 - r;
    for (let k = 1; k < r; k++) for (const [sx, sy] of f & 1 ? [[1, 1], [-1, 1], [1, -1], [-1, -1]] : [[1, 0], [-1, 0], [0, 1], [0, -1]])
      p.set(cx + sx * (f & 1 ? Math.round(k * 0.7) : k), cy + sy * (f & 1 ? Math.round(k * 0.7) : k), '#7c829c');
    p.set(cx, cy, '#c8ccd8');
  };
  wheel(6, 5);
  if (cargo) wheel(fw, 4); else wheel(fw, 5);
  const F = P.bike;
  p.line(6, WY, 13, WY, F); p.line(6, WY, 11, 17, F); p.line(13, WY, 11, 16, F);                   // stays, seat tube
  if (cargo) {
    p.line(13, WY, 22, WY - 1, F); p.line(20, 11, 20, WY - 2, F); p.set(19, 11, T); p.set(18, 11, T);
    for (let y = 13; y <= WY - 1; y++) for (let x = 22; x <= 40; x++) {                               // the box: plywood, a dark rim
      const edge = y === 13 || x === 22 || x === 40 || y === WY - 1;
      p.set(x + (y > 20 ? -Math.min(2, y - 20) * (x > 31 ? 1 : 0) : 0), y, edge ? '#3a2c24' : (x + y) % 7 === 0 ? '#9a7850' : '#b8925e');
    }
    for (const [kx, hair, coat] of [[27, '#d8b46a', '#d8303a'], [34, '#4a3020', '#3a8a5a']]) {         // two children in the box
      p.fill(kx, 8, 3, 2, hair); p.set(kx + 1, 9, '#f4d0b0'); p.set(kx + 2, 9, '#f4d0b0'); p.set(kx + 2, 10, '#f4d0b0'); p.set(kx + 1, 10, '#f4d0b0');
      p.fill(kx - 1, 11, 5, 2, coat);
    }
    p.set(41, 15, '#fff4d0');
  } else {
    p.line(13, WY, 20, 14, F); p.line(11, 17, 20, 15, F); p.line(20, 13, fw, WY, F);                 // down tube, top tube, fork
    p.set(20, 12, F); p.set(19, 11, T); p.set(18, 11, T); p.set(22, 15, '#fff4d0');
    if (P.basket) { p.fill(22, 9, 5, 4, '#8a6a44'); for (let x = 22; x <= 26; x++) p.set(x, 9, '#b08c5e'); p.set(23, 8, '#e86a8a'); p.set(25, 8, '#f6d040'); p.set(24, 7, '#3a8a4a'); p.set(26, 8, '#3a8a4a'); }
  }
  p.fill(9, 15, 4, 1, T); p.set(1, 21, '#ff3a30'); p.set(2, 20, F); p.set(3, 19, F);                // saddle, rear light
  if (P.kid) {                                                                                       // a child seat on the rack
    p.fill(3, 13, 4, 5, '#3a3e52'); p.fill(4, 8, 3, 2, '#e8d08a'); p.set(5, 9, '#f4d0b0'); p.set(6, 9, '#f4d0b0'); p.fill(3, 10, 4, 4, P.kid); p.set(5, 7, '#f6d040'); p.set(4, 7, '#f6d040'); p.set(6, 7, '#f6d040');
  }
  // the rider: far leg, torso, head, near leg, arm
  const coatD = mixHex(P.coat, '#0a0c1c', 0.4), coatL = mixHex(P.coat, '#ffe2b0', 0.25), legsD = mixHex(P.legs, '#05060c', 0.45);
  const legTo = (a, col) => {
    const px0 = 13 + Math.round(2.4 * Math.cos(a)), py = WY + Math.round(2.4 * Math.sin(a)), kx = Math.round((11 + px0) / 2 + 2.5), ky = Math.round((14 + py) / 2 - 0.5);
    for (const o of [0, 1]) { p.line(11 + o, 14, kx + o, ky, col); p.line(kx + o, ky, px0 + o, py - 1, col); }
    p.fill(px0 - 1, py, 4, 1, '#141018');
  };
  legTo(f * TAU / 4 + Math.PI, legsD);
  for (let j = 0; j <= 8; j++) { const x = 11 + Math.round(3 * j / 8), y = 14 - j; p.set(x - 1, y, coatD); p.set(x, y, P.coat); p.set(x + 1, y, P.coat); p.set(x + 2, y, coatL); }
  if (P.pack) p.fill(8, 7, 3, 6, P.pack);
  for (let y = 1; y <= 4; y++) for (let x = 13; x <= 16; x++) p.set(x, y, y > 1 && x > 14 ? P.skin : P.hair);
  p.set(14, 5, P.skin); p.set(15, 5, P.skin);
  if (P.helmet) { for (let x = 12; x <= 17; x++) p.set(x, 1, P.helmet); for (let x = 13; x <= 16; x++) p.set(x, 0, P.helmet); }
  if (P.longHair) for (let y = 2; y <= 8; y++) p.set(12 - (y > 5 ? 1 : 0), y, P.hair);
  legTo(f * TAU / 4, P.legs);
  p.line(15, 7, cargo ? 19 : 18, 10, coatD); p.set(cargo ? 19 : 18, 10, P.skin);
  return p.done();
}
const RIDERS = [
  { coat: '#c8423a', legs: '#2a3458', hair: '#d8b46a', skin: '#f4d0b0', bike: '#1e2230', basket: true, longHair: true },
  { coat: '#2a3a5a', legs: '#1e2230', hair: '#2a1a14', skin: '#e8b890', bike: '#8a8e9e', helmet: '#e8e0d0', pack: '#c8782a' },
  { coat: '#e8e0d0', legs: '#3a5a8a', hair: '#8a3a22', skin: '#f0c4a0', bike: '#3a8a5a', kid: '#e87a3a', longHair: true },
  { coat: '#e8c040', legs: '#262a3a', hair: '#1a1418', skin: '#d8a078', bike: '#b8323a' },
  { coat: '#5a9ab8', legs: '#2a2420', hair: '#6a4a2a', skin: '#f0c4a0', bike: '#1e2230', cargo: true },
  { coat: '#d86a8a', legs: '#2a3458', hair: '#1a1418', skin: '#b07850', bike: '#e8e0d0', basket: true, longHair: true },
  { coat: '#3a8a5a', legs: '#3a3a44', hair: '#c8c4c0', skin: '#e8b890', bike: '#2f5fa8', helmet: '#d8303a' },
  { coat: '#1e2230', legs: '#2a3458', hair: '#4a3020', skin: '#f4d0b0', bike: '#c8a24a', pack: '#3a8a5a' },
];
// the stream on the cycle lane: [rider, crossings per loop, phase, direction, wheel row]; a sixth entry, the crowd rank
// of the rider and bike (with the children they carry), is added right below
const BIKES = [[0, 9, 0.05, 1, 243], [1, 12, 0.4, -1, 247], [2, 8, 0.62, 1, 244], [3, 11, 0.83, -1, 246], [4, 7, 0.3, -1, 248],
  [5, 10, 0.16, 1, 242], [6, 13, 0.7, 1, 245], [7, 9, 0.52, -1, 247]];
BIKES.forEach((q, i) => q.push(rankOf(i, 0.5)));
const BSPAN = W + 100;
let BIKE_SPR;

/* ================= The far quay's people: strollers, waiters, people sitting on the coping (tiny, facing us) ================= */
function makeTiny(coat, hair, legs, f, flip) {
  const p = pix(5, 10), d = flip ? -1 : 1, X = i => (flip ? 4 - i : i);
  p.set(X(1), 0, hair); p.set(X(2), 0, hair); p.set(X(1), 1, hair); p.set(X(2), 1, '#e8b890');
  for (let y = 2; y <= 6; y++) for (let i = 1; i <= 3; i++) p.set(X(i), y, i === 3 ? mixHex(coat, '#ffd9a0', 0.3) : i === 1 && y > 2 ? mixHex(coat, '#0a0c1c', 0.35) : coat);
  if (f) { p.set(X(2), 7, legs); p.set(X(2), 8, legs); p.set(X(2), 9, '#141018'); p.set(X(3), 7, legs); }
  else { p.set(X(1), 7, legs); p.set(X(3), 7, legs); p.set(X(1), 8, legs); p.set(X(3), 8, legs); p.set(X(0), 9, '#141018'); p.set(X(4), 9, '#141018'); }
  void d;
  return p.done();
}
function makeFarSitter(coat, hair, legs, f) {
  const p = pix(4, 9);
  p.set(1, 0, hair); p.set(2, 0, hair); p.set(1, 1, '#e8b890'); p.set(2, 1, '#e8b890');
  for (let y = 2; y <= 4; y++) for (let i = 0; i < 4; i++) p.set(i, y, i === 0 ? mixHex(coat, '#0a0c1c', 0.3) : coat);
  for (let i = 0; i < 4; i++) p.set(i, 5, legs);
  p.set(0, 6, legs); p.set(3, 6, legs); p.set(0, 7, f ? legs : '#e8b890'); p.set(3, 7, f ? '#e8b890' : legs);
  p.set(f ? 0 : 3, 8, '#f4f0e8');
  return p.done();
}
const FAR_WALK = [], FAR_SIT = [];                    // { spr: [[r, l] x 2 frames], k, ph, dir, y, rank } and { spr: [f0, f1], x, k, ph, rank }
function buildFarPeople() {
  const r = rng(7117);
  FAR_WALK.length = 0; FAR_SIT.length = 0;
  for (let i = 0; i < 22; i++) {
    const coat = COATS[Math.floor(r() * COATS.length)], hair = HAIRS[Math.floor(r() * HAIRS.length)], legs = ['#1e2230', '#2a3458', '#3a3a44', '#e8dcc0'][Math.floor(r() * 4)];
    FAR_WALK.push({ spr: [0, 1].map(f => [makeTiny(coat, hair, legs, f, false), makeTiny(coat, hair, legs, f, true)]),
      k: 3 + Math.floor(r() * 4), ph: r(), dir: r() < 0.5 ? 1 : -1, y: FQ - 1 + (i % 3), rank: rankOf(i, 0.25) });
  }
  const gaps = [[74, 142], [326, 344], [414, 428]];                                  // between the moored ships
  for (const [a, b] of gaps) for (let x = a + 2; x < b - 4; x += 6 + Math.floor(r() * 4)) {
    if (FAR_LAMPS.some(l => Math.abs(l - x - 2) < 3)) continue;
    const coat = COATS[Math.floor(r() * COATS.length)], hair = HAIRS[Math.floor(r() * HAIRS.length)], legs = ['#1e2230', '#2a3458', '#3a5a8a'][Math.floor(r() * 3)];
    FAR_SIT.push({ spr: [makeFarSitter(coat, hair, legs, 0), makeFarSitter(coat, hair, legs, 1)], x, k: 40 + Math.floor(r() * 50), ph: r(), rank: rankOf(FAR_SIT.length, 0.55) });
  }
}
// (the waiters go home as the terraces empty: the one before the landmark houses is the last)
const WAITERS = [{ cx: 96, ax: 30, k: 5, ph: 0.1, rank: 0.4 }, { cx: 236, ax: 44, k: 4, ph: 0.6, rank: 0.1 }, { cx: 420, ax: 36, k: 6, ph: 0.35, rank: 0.7 }];
const SIDE_RANK = 0.55;                               // the walker far up the side street

/* ================= The guitarist on a crate (facing left), strumming ================= */
const GUITAR_KEY = { h: '#2a1a14', f: '#e8b890', c: '#3a5a8a', C: '#263c62', g: '#c8863e', G: '#8a5226', n: '#5a3a22', o: '#1a1418',
  l: '#2a2e3e', s: '#141018', k: '#8a6a44', K: '#5a4430', a: '#e8b890', t: '#c8a24a' };
const GUITARIST = [
  ['....ttttt.....', '...tttttt.....', '...ffhhh......', '...fffhh......', '....ffh.......', '....cccc......', '...cccccC.....', 'nnnnggcccC....',
    '..nngGGgccC...', '...gGoGgacC...', '...ggGggccC...', '....ggglllC...', '....lllllll...', '...lll.kkkkk..', '...ll..kKkKk..', '...ll..kkkkk..', '..sss..kKkKk..'],
  ['....ttttt.....', '...tttttt.....', '...ffhhh......', '...fffhh......', '....ffh.......', '....cccc......', '...cccccC.....', 'nnnnggcccC....',
    '..nngGGgccC...', '...gGoGgccC...', '...ggGagccC...', '....ggglllC...', '....lllllll...', '...lll.kkkkk..', '...ll..kKkKk..', '...ll..kkkkk..', '.sss...kKkKk..'],
];

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Copenhagen's own drifting thing is the child's soap bubbles: they show only with the city's own weather. Far drops fall
// in front of the houses and end on the north quay, mid ones land on the canal, near ones on our quay's cobbles.
const WEATHER = weatherPlans(['bubbles']);
const WX_LAND = [[HB + 2, FEDGE], [WL + 2, NEDGE - 2], [NEDGE + 4, H - 1]];
const WX_FAR = FAR_LAMPS.map(x => ({ x, y: 146, rx: 15, ry: 16, col: '#ffd890' }));
const WX_NEAR = [...NEAR_LAMPS.map(l => ({ x: l.x, y: l.y + 8, rx: 30, ry: 34, col: '#ffd08c' })), { x: CART.x + 30, y: CART.y + 18, rx: 34, ry: 22, col: '#ffc070' }];
// (a little thinner than the engine's default: every drop shows here, no foreground shelters the frame)
const wxDepths = far => [far, far, WX_NEAR].map(lights => ({ land: WX_LAND, lights, density: 0.75 }));
const WX = wxDepths(WX_FAR);
const WX_BB = wxDepths([...WX_FAR, { x: BB.x + (BB.w >> 1), y: BB.y + 4, rx: 26, ry: 14, col: '#ffe0a0' }]);   // + the sign's lamps

/* ================= Prerendered state ================= */
// (QUAY_LV[n], MIRROR_LV[n]: the north quay and its mirror with n classes of tables taken, the last = QUAY_C, MIRROR_C;
// TOUR_C[n - 1]: the tour boat with n passenger classes aboard, [facing right, facing left]; PICNIC_C[n]: the picnic
// boat with n of its two extra friends. For crowd 0: BAL_SOLO, the balloon with her pilot alone; TOUR_BARE, the tour
// boat with only her skipper; JAZZ_BARE, the barge with her bulbs and an empty deck; PICNIC_SOLO, the picnic boat with
// only the one at the tiller)
let BAL_SOLO, TOUR_BARE, JAZZ_BARE, PICNIC_SOLO;
let HL, SKY_C, WISPS, BAL_C, GULL_SPR, SWIFT_SPR, ROW_C, QUAY_C, QUAY_LV, BOARD_C, AD_C, PITCH, TOUR_C, JAZZ_C, KAYAK_C, PICNIC_C,
  SWAN_SPR, CYG_SPR, WATER_C, MIRROR_C, MIRROR_LV, NEARQ_C, NLAMP_C, CART_C, LSHIP_C, BUB_SPR, GUITAR_SPR, VIG_C, DOG_SPR, NOTE_SPR;
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.46) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [10, 14, 40], Math.round(q * 0.34 * 255));
  }
  return p.done();
}
const DOG = [['.......ee.', '......dddd', 'tddddddd..', '.dddddd...', '.d.d..d.d.'], ['.......ee.', '......dddd', 'tddddddd..', '.dddddd...', '..dd..dd..']];
function prerender() {
  WINS.length = 0; SIGNS.length = 0; CANDLES.length = 0; TOASTERS.length = 0; TABLES.length = 0; TOUR.seats.length = 0; JZ.bulbs.length = 0;
  HL = {
    win: makeHalo(6, '#ffb860', 0.1, 1.4), heater: makeHalo(10, '#ffb060', 0.26, 1.5), farLamp: makeHalo(20, '#ffc878', 0.22, 1.6),
    core: makeHalo(6, '#fff0c8', 0.5, 1.3), nearLamp: makeHalo(46, '#ffc070', 0.2, 1.6), nearCore: makeHalo(11, '#fff0c8', 0.5, 1.3),
    cart: makeHalo(28, '#ffb860', 0.2, 1.5), bb: makeHalo(30, '#ffd9a0', 0.1, 1.6), bbLamp: makeHalo(7, '#ffe0a0', 0.42, 1.3),
    flash: makeHalo(15, '#ffffff', 0.75, 1.2), bulb: makeHalo(3, '#ffc870', 0.45, 1.1), jazz: makeHalo(44, '#ffb860', 0.16, 1.5),
    burner: makeHalo(16, '#ffb040', 0.36, 1.4), lantern: makeHalo(11, '#ffc878', 0.3, 1.4), tiny: makeHalo(4, '#fff4d0', 0.5, 1.2),
    tail: makeHalo(3, '#ff3a30', 0.5, 1), side: makeHalo(9, '#ffd890', 0.3, 1.4), glow: makeHalo(60, '#ffb070', 0.1, 1.7),
  };
  SKY_C = renderSky();
  WISPS = WISP_DEFS.map(d => ({ c: makeWisps(d.seed, d.h, d.n, d.pal, d.a), y: d.y, k: d.k }));
  BAL_C = [renderBalloon(false), renderBalloon(true)]; BAL_SOLO = [renderBalloon(false, 1), renderBalloon(true, 1)];
  GULL_SPR = GULL_F.map(rows => [sprite(rows, GULL_KEY), sprite(rows, GULL_KEY, true)]);
  SWIFT_SPR = SWIFT_F.map(rows => sprite(rows, { k: '#1a1e3a' }));
  ROW_C = renderRow(); ROW_A = ROW_C.getContext('2d').getImageData(0, 0, W, HB + 1).data;
  QUAY_C = renderFarQuay();
  const empty = renderFarQuay(false);
  QUAY_LV = [empty];
  for (let n = 1; n < TERR_CLS; n++) QUAY_LV.push(quayLevel(empty, n));
  QUAY_LV.push(QUAY_C);
  BOARD_C = renderBoard(); AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#ff8a00' });
  SHIPS.forEach(makeShip);
  TOUR_C = [];
  for (let n = 1; n <= TOUR_CLS; n++) { const c = renderTourBoat(n); TOUR_C.push([c, flipC(c)]); }
  { const c = renderTourBoat(0); TOUR_BARE = [c, flipC(c)]; }
  JAZZ_C = [renderJazz(0), renderJazz(1)]; JAZZ_BARE = renderJazz(0, false);
  KAYAK_C = [['#f0c030', '#e8562a'], ['#d8303a', '#2f6ab8']].map(([hull, vest]) => [0, 1, 2, 3].map(f => renderKayak(f, hull, vest)));
  PICNIC_C = [0, 1, 2].map(n => renderPicnic(n)); PICNIC_SOLO = renderPicnic(0, false);
  SWAN_SPR = [sprite(SWAN, SWAN_KEY), sprite(SWAN, SWAN_KEY, true)]; CYG_SPR = [sprite(CYGNET, SWAN_KEY), sprite(CYGNET, SWAN_KEY, true)];
  WATER_C = renderWater(); MIRROR_C = renderMirror();
  MIRROR_LV = QUAY_LV.map((q, n) => (n === TERR_CLS ? MIRROR_C : renderMirror(q, MIRROR_C)));
  NEARQ_C = renderNearQuay(); NLAMP_C = renderNearLamps(); CART_C = renderCart(); LSHIP_C = renderMuseumShip();
  BUB_SPR = [1, 2, 3, 4].map(makeBubble);
  buildPeople(); buildFarPeople();
  SIT_SPR = SITTERS.map(S => Object.fromEntries(SIT_POSES.map(pose => [pose, makeSitter(S, pose)])));
  BIKE_SPR = RIDERS.map(P => [0, 1, 2, 3].map(f => { const c = makeCyclist(P, f); return [c, flipC(c)]; }));
  GUITAR_SPR = GUITARIST.map(rows => sprite(rows, GUITAR_KEY));
  DOG_SPR = DOG.map(rows => { const k = { e: '#3a2618', d: '#c89a5a', t: '#c89a5a' }; return [sprite(rows, k), sprite(rows, k, true)]; });
  NOTE_SPR = [sprite(['.11', '.1.', '.1.', '11.', '11.'], { 1: '#fff0c8' }), sprite(['.111', '.1.1', '.1.1', '11.11', '11.11'], { 1: '#ffd88a' })];
  VIG_C = renderVignette();
}

/* ================= Per-frame: the sky ================= */
function drawSky(t, crowd) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#eef2ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  for (const L of WISPS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, -off, L.y); ctx.drawImage(L.c, W - off, L.y); }
  const b = balloonAt(t);
  if (b) {
    const on = burner(t), X = Math.round(b.x), Y = Math.round(b.y);
    ctx.drawImage((nobody(crowd) ? BAL_SOLO : BAL_C)[on ? 1 : 0], X, Y);
    if (on) halo(HL.burner, X + 11, Y + 18, 0.8 + 0.2 * hash(step(t, 2400), 9));
  }
  SWIFTS.forEach((s, i) => {
    const a = TAU * (s.k * t / LOOP + s.ph), x = s.cx + s.ax * Math.cos(a) + 40 * Math.sin(TAU * (s.k2 * t / LOOP + s.ph)), y = s.cy + s.ay * Math.sin(a);
    ctx.drawImage(SWIFT_SPR[(step(t, 1440) + i) & 1], Math.round(x) - 2, Math.round(y));
  });
}
function drawGull(g, t, i) {
  const { x, y, vx } = birdAt(g, t), fr = [0, 1, 2, 1][(step(t, 720) + i) & 3];
  ctx.drawImage(GULL_SPR[Math.abs(vx) < 0.25 ? 1 : fr][vx >= 0 ? 0 : 1], Math.round(x) - 4, Math.round(y) - 2);
}

/* ================= Per-frame: the houses' windows, the side street, the flag on the corner house ================= */
const winLit = (w, t) => w.mode === 'on' || (w.mode === 'tog' && frac(w.k * t / LOOP + w.ph) < w.duty);
function drawRow(t, crowd) {
  ctx.drawImage(ROW_C, 0, 0);
  for (let i = 0; i < WINS.length; i++) {
    const w = WINS[i];
    if (!winLit(w, t)) continue;
    const half = w.h >> 1;
    ctx.fillStyle = w.c1; ctx.fillRect(w.x, w.y, w.w, half);
    ctx.fillStyle = w.c2; ctx.fillRect(w.x, w.y + w.h - half, w.w, half);
    if (i % 3 === 0) halo(HL.win, w.x + 2, w.y + 3, 0.8);
  }
  halo(HL.side, 330, 127, 0.8 + 0.1 * wave(t, 37));
  if (SIDE_RANK < crowd) {                                                              // someone far up the side street
    const sx = Math.round(330 + 5 * Math.sin(TAU * 3 * t / LOOP)), sf = step(t, 480) & 1;
    box(sx, 146, 1, 4, '#2a2e4a'); box(sx, 145, 1, 1, '#e8b890'); box(sx + (sf ? 1 : 0), 150, 1, 1, '#141018');
  }
  for (const [fx, fy, ph] of [[HOUSES[7].x0 + 16, HOUSES[7].eave + 14, 0.3], [HOUSES[1].x0 + 37, HOUSES[1].eave + 17, 0.7]]) {   // flags on staffs from the fronts
    box(fx, fy - 1, 1, 8, '#c4c8da'); drawFlag(fx, fy, t, ph, -1);
  }
}
/** a Dannebrog streaming from a staff at (x, y) towards dir, 8 x 5 */
function drawFlag(x, y, t, ph, dir) {
  for (let i = 0; i < 8; i++) {
    const dy = Math.round(Math.sin(TAU * (64 * t / LOOP + ph) - i * 0.9) * (0.3 + i * 0.16)), X = x + dir * (i + 1);
    ctx.fillStyle = i === 2 ? '#f6f2ea' : '#d2303a'; ctx.fillRect(X, y + dy, 1, 5);
    if (i !== 2) { ctx.fillStyle = '#f6f2ea'; ctx.fillRect(X, y + dy + 2, 1, 1); }
  }
}
/** a long pennant from a masthead */
function drawPennant(x, y, t, ph) {
  for (let i = 0; i < 10; i++) {
    const dy = Math.round(Math.sin(TAU * (80 * t / LOOP + ph) - i * 0.7) * (0.2 + i * 0.14));
    ctx.fillStyle = i < 3 && i !== 1 ? '#d2303a' : i === 1 ? '#f6f2ea' : i % 2 ? '#d2303a' : '#e85a50';
    ctx.fillRect(x - 1 - i, y + dy, 1, i < 5 ? 2 : 1);
  }
}

/* ================= Per-frame: the roof sign ================= */
const BB_FLICK = [[64.2, 65.6], [187.4, 188.3]];
const bbLevel = t => { for (const [a, b] of BB_FLICK) if (t >= a && t < b) return hash(step(t, 4800), 13) < 0.45 ? 0.15 : 0.8; return 1; };
function drawBoard(t, pitch) {
  const lv = bbLevel(t);
  halo(HL.bb, BB.x + (BB.w >> 1), BB.y + 3, (0.86 + 0.1 * wave(t, 6, 0.3)) * (2 + lv) / 3);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    const s = step(t, 48) % 6, u = frac(t * 48 / LOOP), drop = Math.round(-6 * (1 - Math.min(1, u * 7)) ** 2);
    for (let k = 0; k < Math.min(s, 5); k++) for (const [dx, dy, col] of TOPPINGS[k]) {
      const y = BB.y + dy + (k === s - 1 ? drop : 0);
      if (y > BB.y) { ctx.fillStyle = col; ctx.fillRect(BB.x + dx, y, 1, 1); }
    }
    if (s === 5 && (step(t, 960) & 1)) { dot(BB.x + 14, BB.y + 5, '#fff8d0'); dot(BB.x + 2, BB.y + 7, '#fff8d0'); }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => halo(HL.bbLamp, lx, BB_LAMP_Y + 1, (i === 2 ? lv : 1) * (0.9 + 0.08 * wave(t, 500 + i * 41))));
}

/* ================= Per-frame: the north quay (terraces, strollers, waiters, people on the coping) ================= */
function drawFarQuay(t, crowd, terr) {
  blit(QUAY_LV[terr]);
  const jz = jazzMood(t);
  PARASOLS.forEach((P, i) => halo(HL.heater, P.x, 148, 0.8 + 0.12 * wave(t, 23 + i * 3, i * 0.37)));
  CANDLES.forEach((c, i) => { ctx.globalAlpha = 0.6 + 0.4 * hash(i, step(t + i * 0.13, 1200), 21); dot(c.x, c.y, '#fff2c8'); });
  ctx.globalAlpha = 1;
  const s8 = step(t, 160);
  TOASTERS.forEach((q, i) => { if (q.rank < crowd && hash(i, s8, 31) < 0.3) { dot(q.x, q.y - 1, '#f6e8c0'); dot(q.x, q.y, '#e8b890'); } });
  for (const w of WAITERS) {                                                         // waiters, a tray held high
    if (w.rank >= crowd) continue;
    const a = TAU * (w.k * t / LOOP + w.ph), x = Math.round(w.cx + w.ax * Math.sin(a)), d = Math.cos(a) >= 0 ? 1 : -1, f = Math.floor(x / 2) & 1;
    box(x, 148, 2, 1, '#2a1a14'); box(x, 149, 2, 1, '#e8b890'); box(x - 1, 150, 3, 4, '#f4f0e8'); box(x - 1, 153, 3, 3, '#16182a');
    box(x - 1 + f, 156, 1, 2, '#16182a'); box(x + 1 - f, 156, 1, 1, '#16182a');
    box(x + d * 2, 148, 1, 2, '#f4f0e8'); box(x + d * 2 - 1, 147, 4, 1, '#c8ccd8'); dot(x + d * 2, 146, '#e8c040'); dot(x + d * 2 + 1, 146, '#9ad0a8');
  }
  for (const p of FAR_WALK) {
    if (p.rank >= crowd) continue;
    const u = frac(p.k * t / LOOP + p.ph), x = Math.round(p.dir > 0 ? -8 + u * (W + 16) : W + 8 - u * (W + 16));
    ctx.drawImage(p.spr[Math.floor(u * (W + 16) / 3) & 1][p.dir > 0 ? 0 : 1], x - 2, p.y - 9);
    if (jz > 0.5 && hash(p.k, Math.round(p.ph * 97), 3) < 0.4) dot(x + p.dir, p.y - 10 - (step(t, 720) & 1), '#e8b890');   // a wave for the band
  }
  for (const s of FAR_SIT) if (s.rank < crowd) ctx.drawImage(s.spr[frac(s.k * t / LOOP + s.ph) < 0.5 ? 0 : 1], s.x, FEDGE - 6);
  FAR_LAMPS.forEach((lx, i) => { const I = 0.9 + 0.07 * wave(t, 300 + i * 7, i * 0.3); halo(HL.farLamp, lx, 143, I); halo(HL.core, lx, 143, I * 0.8); });
}

/* ================= Per-frame: the moored ships (hulls and rigs stand still; lanterns, pennants, flags and the sail move) ================= */
// (crowd 0: no hands aboard, so the ketch's sail stays furled and her lantern low, and the schooner's deck is empty)
function drawShips(t, crowd) {
  const crew = !nobody(crowd);
  SHIPS.forEach((S, i) => {
    blit(S.hullC);
    const lit = S.sail ? 0.4 + (crew ? 0.6 * smooth(SAIL.pre, SAIL.pre + 6, t) * (1 - smooth(SAIL.dn1 + 4, SAIL.dn1 + 10, t)) : 0) : 0.55 + 0.1 * wave(t, 40 + i * 9);
    dot(S.lantern.x, S.lantern.y, '#fff2c8'); halo(HL.lantern, S.lantern.x, S.lantern.y, lit);
    if (S.sail) { drawSail(S, t, crew ? sailUp(t) : 0); if (crew) drawSailors(S, t); }
    if (i === 1 && crew) {                                                           // a deckhand on the schooner, tidying up
      const dx = Math.round(S.x0 + 34 + 16 * Math.sin(TAU * 4 * t / LOOP) + 5 * Math.sin(TAU * 11 * t / LOOP)), bend = hash(step(t, 96), 41) < 0.35 ? 2 : 0;
      box(dx, DECK - 10 + bend, 2, 1, '#c8423a'); box(dx, DECK - 9 + bend, 2, 1, '#e8b890'); box(dx - 1, DECK - 8 + bend, 3, 4 - bend, '#e8c040'); box(dx - 1, DECK - 4, 1, 3, '#1e2230'); box(dx + 1, DECK - 4, 1, 3, '#16182a');
    }
    blit(S.rig);
    for (const m of S.tops) drawPennant(m.x, m.y + 1, t, i * 0.23 + m.x * 0.01);
    if (S.staff.x > 12 && S.staff.x < W - 2) drawFlag(S.staff.x, S.staff.y, t, i * 0.31, -1);
  });
}
/** g: how far she is up, 0 furled .. 1 set */
function drawSail(S, t, g) {
  const M = S.sail, bx = M.x + (M.aft > 0 ? 2 : -1), bl = M.bl, boomY = M.boomY;
  const X = q => bx + M.aft * Math.round(q);
  if (g < 0.04) {                                                                   // furled on the boom, the gaff on top
    const x = Math.min(X(1), X(bl - 2));
    box(x, boomY - 2, bl - 2, 2, SAILC[2]); box(x + 1, boomY - 3, bl - 5, 1, SAILC[3]); box(x, boomY - 4, bl - 4, 1, '#7a5a3c');
    for (let q = 3; q < bl - 2; q += 5) box(X(q), boomY - 3, 1, 3, '#3a2a26');
    return;
  }
  const gl = bl - 5, ang = 0.66 * g, ty = Math.round(lerp(boomY - 5, M.hounds + 5, g));
  const pq = gl * Math.cos(ang), py = Math.round(ty - gl * Math.sin(ang)), shake = g < 1 ? 1 : 0;
  for (let y = py; y < boomY; y++) {
    const f = (y - py) / (boomY - py);
    let qR = lerp(pq, bl - 1, f), qL = y >= ty ? 1 : lerp(pq, 0, (y - py) / Math.max(1, ty - py));
    if (shake) qR += Math.sin(y * 0.9 + t * 9) * 0.8 * (1 - g);                       // the leech flaps until she is sheeted in
    const x0 = X(qL), x1 = X(qR), xa = Math.min(x0, x1), n = Math.abs(x1 - x0) + 1;
    const wr = shake && (y + Math.floor(t * 6)) % 6 === 0;
    ctx.fillStyle = SAILC[wr ? 1 : f > 0.72 ? 3 : 2]; ctx.fillRect(xa, y, n, 1);
    ctx.fillStyle = SAILC[f > 0.72 ? 4 : 3]; ctx.fillRect(x1, y, 1, 1);
    ctx.fillStyle = SAILC[1];                                                         // the cloths' seams
    for (let q = 5; q < qR - 1; q += 5) if (q > qL) ctx.fillRect(X(q), y, 1, 1);
    if ((y - py) % 14 === 9 && !shake) { ctx.fillStyle = SAILC[1]; ctx.fillRect(xa + 1, y, Math.max(1, n - 2), 1); }   // reef bands
  }
  ctx.fillStyle = '#7a5a3c';
  for (let k = 0; k <= gl; k++) ctx.fillRect(X(pq * k / gl), Math.round(lerp(ty, py, k / gl)) - 1, 1, 1);   // the gaff
}
/** two hands come out of the deckhouse, haul on the halyards and go below again */
function drawSailors(S, t) {
  if (t < SAIL.pre + 1 || t >= SAIL.dn1 + 9) return;
  const M = S.sail, home = S.lantern.x - 6, hauling = (t >= SAIL.up0 && t < SAIL.up1) || (t >= SAIL.dn0 && t < SAIL.dn1);
  [[M.x - 4, 0], [M.x + 5, 1.5]].forEach(([post, lag], i) => {
    const s = along([[SAIL.pre + 1 + lag, home, 0], [SAIL.pre + 9 + lag, post, 0], [SAIL.dn1 + 1 + lag, post, 0], [SAIL.dn1 + 8 + lag, home, 0]], t);
    const x = Math.round(s.x), y = DECK - 10, pull = hauling && ((step(t, 480) + i) & 1);
    box(x, y, 2, 1, i ? '#d8b46a' : '#2a1a14'); box(x, y + 1, 2, 1, '#e8b890'); box(x - 1, y + 2 + (pull ? 1 : 0), 3, 4, i ? '#e8e0d0' : '#2f5fa8');
    box(x - 1 + (s.moving && (Math.floor(s.x) & 1) ? 1 : 0), y + 6, 1, 3, '#1e2230'); box(x + 1, y + 6, 1, 3, '#16182a');
    if (hauling) { const hx = post < M.x ? x + 2 : x - 2; box(hx, y - 1 + (pull ? 3 : 0), 1, 3, '#e8b890'); }
  });
}
/** how much the quays are looking at the band on the barge: 0 .. 1 */
const jazzMood = t => smooth(JAZZ.pre, JAZZ.pre + 6, t) * (1 - smooth(JAZZ.t1 - 6, JAZZ.t1, t));

/* ================= Per-frame: the canal, and everything that floats ================= */
function drawCanal(t, mirror) {
  ctx.drawImage(WATER_C, 0, WL);
  for (let k = 0, rows = NEDGE - WL; k < rows; k++) {                                // the mirror image, broken into rippling slivers
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.6 + k * 0.05) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.6);
    ctx.globalAlpha = 0.8 * (1 - k / (rows + 16)) * (0.82 + 0.18 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(mirror, 0, k, W, 1, off, WL + k, W, 1);
  }
  for (const g of GLINTS) {
    const v = wave(t, g.kf, g.ph);
    if (v < -0.1) continue;
    ctx.globalAlpha = 0.42 * Math.min(1, v + 0.1);
    ctx.fillStyle = g.c; ctx.fillRect(Math.round(frac(g.x + g.k * t / LOOP) * (W + 30)) - 15, g.y, g.len, 1);
  }
  ctx.globalAlpha = 1;
  FAR_LAMPS.forEach((lx, i) => streak(ctx, t, lx, 3, '#ffd890', 0.42, 1 + i, WL + 1, NEDGE));
  NEAR_LAMPS.forEach((l, i) => streak(ctx, t, l.x, 4, '#ffc878', 0.2, 7 + i, NEDGE - 16, NEDGE));
}
/** a sprite sitting on the water at row wy, with its broken mirror image under it */
function drawFloat(spr, x, wy, t, a = 0.34) {
  const w = spr.width, h = spr.height, n = Math.min(h, 12);
  for (let j = 0; j < n; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 2.3 + wy) * (0.5 + j * 0.09));
    ctx.globalAlpha = a * (1 - j / n);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wy + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, wy - h + 1);
}
/** foam and ripples trailing from a stern at x, behind a boat heading dir */
function drawWake(x, wy, dir, n, t) {
  const st = step(t, 1440);
  for (let k = 0; k < n; k++) {
    if (hash(k, st, 17) > 0.84 - k * 0.012) continue;
    ctx.globalAlpha = 0.6 * (1 - k / n);
    dot(x - dir * k * 3, wy + 1 + k * 0.3, k < 5 ? '#eef2fc' : '#a8bce0');
    if (k > 2) dot(x - dir * (k * 3 + 1), wy - k * 0.14, '#8ea6d0');
  }
  ctx.globalAlpha = 1;
}
const TOUR_WY = 187, JAZZ_WY = 194, PICNIC_WY = 181;
function drawTour(t, crowd) {
  const out = t >= TOUR_OUT.t0 && t < TOUR_OUT.t1, back = t >= TOUR_IN.t0 && t < TOUR_IN.t1;
  if (!out && !back) return;
  const E = out ? TOUR_OUT : TOUR_IN, u = (t - E.t0) / (E.t1 - E.t0), w = TOUR.w, lv = tourLevel(crowd);
  const x = Math.round(out ? lerp(-w - 4, W + 4, u) : lerp(W + 4, -w - 4, u)), dir = out ? 1 : -1, top = TOUR_WY - TOUR.h + 1;
  const sx = i => (out ? x + i : x + w - 1 - i);                                      // sprite column -> screen
  drawWake(sx(0) - dir, TOUR_WY, dir, 30, t);
  drawFloat((lv ? TOUR_C[lv - 1] : TOUR_BARE)[out ? 0 : 1], x, TOUR_WY, t);
  dot(sx(w - 2), TOUR_WY - 1, '#eef2fc'); dot(sx(w), TOUR_WY, '#c8d6f0');             // bow wave
  if (lv) {                                                                          // the guide points things out (she has none at crowd 0)
    if (step(t, 120) % 3 !== 2) { box(Math.min(sx(103), sx(105)), top + 5, 3, 1, '#d8303a'); dot(sx(106), top + 4, '#e8b890'); }
    else dot(sx(103), top + 7, '#e8b890');
  }
  const s = step(t, 480), k = Math.floor(hash(s, 5) * TOUR.seats.length);              // someone's camera flashes
  if (hash(s, 6) < 0.45 && frac(t * 2) < 0.4) { const q = TOUR.seats[k]; if (q.cls < lv) { dot(sx(q.x + 1), top + q.y, '#ffffff'); halo(HL.tiny, sx(q.x + 1), top + q.y, 1); } }
  halo(HL.tiny, sx(110), top + 13, 0.8);
}
function drawJazz(t, crowd) {
  const bare = nobody(crowd), pre = smooth(JAZZ.pre + 2, JAZZ.t0, t) * (1 - smooth(JAZZ.t0 + 2, JAZZ.t0 + 9, t));
  if (pre > 0) halo(HL.jazz, -14, JAZZ_WY - 14, pre);                                // her lights show before she does
  if (t < JAZZ.t0 || t >= JAZZ.t1) return;
  const u = (t - JAZZ.t0) / (JAZZ.t1 - JAZZ.t0), x = Math.round(lerp(-JZ.w - 6, W + 6, u)), top = JAZZ_WY - JZ.h + 1;
  halo(HL.jazz, x + 30, top + 24, 1); halo(HL.jazz, x + 60, top + 24, 1);
  drawWake(x - 1, JAZZ_WY, 1, 22, t);
  drawFloat(bare ? JAZZ_BARE : JAZZ_C[step(t, 600) & 1], x, JAZZ_WY, t, 0.4);
  JZ.bulbs.forEach((b, i) => {
    const I = 0.7 + 0.3 * wave(t, 60 + (i * 7) % 23, i * 0.31);
    dot(x + b.x, top + b.y, ['#ffe9b0', '#ff9a5a', '#fff8e0', '#9ad8ff'][b.c]); halo(HL.bulb, x + b.x, top + b.y, I);
  });
  if (bare) return;                                                                  // (no band aboard, no tune)
  for (let i = 0; i < 7; i++) {                                                      // the tune, rising off the deck
    const { cyc, u: v } = cycle(t, 70 + i * 9, i * 0.17), nx = x + 12 + hash(i, cyc, 3) * 60 + wave(t, 31 + i * 4, i * 0.2) * 3 * v;
    ctx.globalAlpha = Math.sin(Math.PI * v) * 0.9;
    ctx.drawImage(NOTE_SPR[(i + cyc) & 1], Math.round(nx), Math.round(top + 6 - v * 22));
  }
  ctx.globalAlpha = 1;
}
function drawKayaks(t, crowd) {
  if (t < KAYAK.t0 || t >= KAYAK.t1 || nobody(crowd)) return;                         // (no paddlers, no kayaks)
  const u = (t - KAYAK.t0) / (KAYAK.t1 - KAYAK.t0), x = Math.round(lerp(W + 6, -76, u)), st = step(t, 600);
  [[0, 197, 0], [44, 201, 2]].forEach(([dx, wy, fo], i) => {
    drawWake(x + dx + 25, wy, -1, 8, t);
    drawFloat(KAYAK_C[i][(st + fo) & 3], x + dx, wy, t, 0.3);
  });
}
function drawPicnic(t, crowd) {
  if (t < PICNIC.t0 || t >= PICNIC.t1) return;
  const u = (t - PICNIC.t0) / (PICNIC.t1 - PICNIC.t0), x = Math.round(lerp(-PICNIC_W - 4, W + 4, u)), top = PICNIC_WY - PICNIC_H + 1;
  drawWake(x - 1, PICNIC_WY, 1, 12, t);
  const solo = nobody(crowd);                                                         // only the one at the tiller: nobody says skål
  drawFloat(solo ? PICNIC_SOLO : PICNIC_C[picnicLevel(crowd)], x, PICNIC_WY, t, 0.3);
  halo(HL.lantern, x + 18, top + 7, 0.8 + 0.15 * wave(t, 90));
  if (!solo && hash(step(t, 96), 12) < 0.4) { dot(x + 13, top + 5, '#f6e8c0'); dot(x + 24, top + 5, '#9ad0a8'); }   // skål
}
function drawSwans(t, crowd) {
  const fed = !nobody(crowd);                                                         // somebody on the coping has bread for them
  for (const s of SWANS) {
    const { x, y, vx } = swanAt(s.lag, t, fed), X = Math.round(x), Y = Math.round(y), d = vx >= 0 ? 0 : 1, spr = (s.big ? SWAN_SPR : CYG_SPR)[d];
    ctx.globalAlpha = 0.28; box(X - (s.big ? 4 : 1), Y + 1, s.big ? 9 : 4, 1, '#dfe8f8');
    if (s.big) { box(X - 3, Y + 2, 6, 1, '#dfe8f8'); ctx.globalAlpha = 0.35; for (let k = 1; k < 6; k++) dot(X + (d ? 1 : -1) * (4 + k * 2), Y + 1 + (k >> 1), '#c4d6f4'); }
    ctx.globalAlpha = 1;
    ctx.drawImage(spr, X - (spr.width >> 1), Y - spr.height + 1);
  }
  if (fed && t >= FEED.t0 + 5 && t < FEED.t1 - 5) {                                   // bread from the quay: crumbs, rings
    for (let i = 0; i < 3; i++) {
      const { cyc, u } = cycle(t, 120 + i * 20, i * 0.3), tx = 250 + hash(i, cyc, 4) * 34;
      dot(lerp(234, tx, u), 199 - Math.sin(Math.PI * u) * 9 + u * 4, '#f0e0b0');
    }
  }
  const ds = t - FEED.splash;
  if (fed && ds >= 0 && ds < 2.2) { ctx.fillStyle = '#dfe8f8'; for (const rr of [1 + ds * 4, ds * 4 - 1]) if (rr > 0) { ctx.globalAlpha = (1 - ds / 2.2) * 0.6; const n = 8 + Math.floor(rr * 2); for (let j = 0; j < n; j++) ctx.fillRect(Math.round(270 + Math.cos(j / n * TAU) * rr), Math.round(202 + Math.sin(j / n * TAU) * rr * 0.36), 1, 1); } ctx.globalAlpha = 1; }
}

/* ================= Per-frame: our quay's edge (lanterns, the people sitting on the coping, the guitarist, friends) ================= */
function sitterPose(S, i, t) {
  if (t >= JAZZ.t0 + 5 && t < JAZZ.t1 - 8 && hash(i, 5) < 0.8) return (step(t, 720) + i) & 1 ? 'cheer' : 'cheer2';   // clapping along
  if (t >= JAZZ.pre + 1 && t < JAZZ.t0 + 5) return i % 3 === 0 ? 'pointL' : 'turnL';
  for (let k = 0; k < TOASTS.length; k++) if (t >= TOASTS[k][0] && t < TOASTS[k][1] && S.g === [2, 1, 4, 0, 3][k]) return 'clink';
  if (t >= SAIL.up0 - 8 && t < SAIL.up1 + 3 && S.x > 200) return S.x < 290 ? (i % 2 ? 'pointR' : 'turnR') : 'turnL';
  if (i === 5 && t >= FEED.t0 + 4 && t < FEED.t1 - 4) return step(t, 240) & 1 ? 'pointR' : 'idle';             // throwing bread
  const h = hash(i, step(t + i * 0.37, 120), 44);
  return h < 0.14 ? 'sip' : h < 0.3 ? 'turnL' : h < 0.46 ? 'turnR' : h < 0.54 ? 'leanL' : h < 0.62 ? 'leanR' : 'idle';
}
/** a standing person from STAND: style st, feet at (x, y), facing dir */
function stander(st, pose, x, y, dir) {
  const spr = STAND[st][pose][dir > 0 ? 0 : 1];
  ctx.drawImage(spr, Math.round(x) - (dir > 0 ? 7 : 9), Math.round(y) - spr.height + 1);
}
function walker(st, x, y, dir, dist) {
  const spr = WALK[st][Math.floor(Math.abs(dist) / (STYLES[st].h * 0.3)) & 3][dir > 0 ? 0 : 1];
  ctx.drawImage(spr, Math.round(x) - (dir > 0 ? 7 : 9), Math.round(y) - spr.height + 1);
}
const photoAt = t => { for (const T of PHOTO) if (t >= T && t < T + 11) return t - T; return -1; };
const GULL_SIT = ['..ww.', '.wwwy', 'gwww.', '.gg..', '..k..'];
const here = (rank, crowd) => !nobody(crowd) && (rank === 0 || rank < crowd);    // rank 0 = the cast that stays while anybody does
const FRIEND_RANK = [0.28, 0, 0.65];                         // the three who pose for the photo: the one in the middle always does
const EATER_RANK = [0.45, 0.8];                              // the two eating by the hot dog wagon
/** the cast by the edge (not at crowd 0): the guitarist and his tune, the friends' photo, the child with the bubble wand */
function drawEdgeCast(t, crowd) {
  // the guitarist: strumming, a foot tapping; the tune drifts up in little notes
  const gf = step(t, 720) & 1;
  ctx.drawImage(GUITAR_SPR[gf], GUITAR.x - 6, GUITAR.y - 16);
  for (let i = 0; i < 3; i++) {
    const { cyc, u } = cycle(t, 48 + i * 12, i * 0.31);
    if (hash(i, cyc, 8) < 0.55) continue;
    ctx.globalAlpha = Math.sin(Math.PI * u) * 0.85;
    ctx.drawImage(NOTE_SPR[(i + cyc) & 1], Math.round(GUITAR.x - 8 - i * 5 + wave(t, 22, i * 0.3) * 2), Math.round(GUITAR.y - 20 - u * 18));
  }
  ctx.globalAlpha = 1;
  // friends having their picture taken by the bollard
  const ph = photoAt(t), posing = ph >= 1 && ph < 9, cheer = jazzMood(t) > 0.6 && t >= JAZZ.t0 + 5;
  stander(9, posing ? 'camera' : cheer ? 'wave' : hash(step(t, 60), 2) < 0.3 ? 'point' : 'idle', 256, 219, 1);
  [[1, 279, 216], [7, 288, 217], [12, 297, 216]].forEach(([st, x, y], i) => {
    if (!here(FRIEND_RANK[i], crowd)) return;
    const pose = posing ? ['wave', 'cheer', 'up'][i] : cheer ? ((step(t, 480) + i) & 1 ? 'cheer' : 'wave') : hash(i, step(t + i, 80), 3) < 0.25 ? 'point' : 'idle';
    stander(st, pose, x, y - (posing && i === 1 && (step(t, 480) & 1) ? 1 : 0), -1);
  });
  if (ph >= 0 && ((ph >= 4 && ph < 4.22) || (ph >= 6 && ph < 6.22))) { halo(HL.flash, 265, 199, 1); box(263, 198, 3, 2, '#ffffff'); }
  // the child with the bubble wand, a parent watching
  const kb = step(t, 240) & 1;
  stander(6, cheer ? 'wave' : hash(step(t, 48), 7) < 0.4 ? 'point' : 'idle', BUBBLE_KID.x + 13, BUBBLE_KID.y + 1, -1);
  stander(14, 'up', BUBBLE_KID.x, BUBBLE_KID.y, -1);
  ctx.fillStyle = '#c8ccd8';
  for (let k = 0; k < 4; k++) ctx.fillRect(BUBBLE_KID.x - 6 - k, BUBBLE_KID.y - 11 - k - kb, 1, 1);
  ctx.fillStyle = '#e8f4ff';
  for (const [dx, dy] of [[0, -1], [1, -2], [2, -2], [3, -1], [3, 0], [2, 1], [1, 1], [0, 0]]) ctx.fillRect(BUBBLE_KID.x - 13 + dx, BUBBLE_KID.y - 16 + dy - kb, 1, 1);
}
function drawEdge(t, crowd) {
  blit(LSHIP_C); drawPennant(LS.x + 2, LS.top - 15, t, 0.6);
  blit(NEARQ_C);
  for (const l of NEAR_LAMPS) { const I = 0.92 + 0.05 * wave(t, 260 + l.x, 0.2); halo(HL.nearLamp, l.x, l.y, I); }
  blit(NLAMP_C);
  for (const l of NEAR_LAMPS) halo(HL.nearCore, l.x, l.y, 0.8);
  SITTERS.forEach((S, i) => { if (here(S.rank, crowd)) ctx.drawImage(SIT_SPR[i][sitterPose(S, i, t)], S.x - SCX, NEDGE - SHT + 1); });
  if (!nobody(crowd)) drawEdgeCast(t, crowd);
  // a gull on the bollard, until a child runs at it (with nobody about it leaves by itself)
  const gx = BOLLARDS[1], gy = NEDGE - 6;
  if (t >= GULL_HOP.land - 3 && t < GULL_HOP.off + 4) {
    if (t < GULL_HOP.land) { const u = (t - GULL_HOP.land + 3) / 3; ctx.drawImage(GULL_SPR[step(t, 960) % 3][1], Math.round(lerp(gx + 90, gx - 4, u)), Math.round(lerp(150, gy - 5, u * u))); }
    else if (t < GULL_HOP.off) {
      const peck = hash(step(t, 240), 17) < 0.3 ? 1 : 0;
      GULL_SIT.forEach((row, j) => { for (let i = 0; i < 5; i++) { const c = { w: '#f0f2f8', g: '#9aa6c4', y: '#f0b040', k: '#e8a040' }[row[i]]; if (c) dot(gx - 2 + i, gy - 5 + j + (j < 2 ? peck : 0), c); } });
    } else { const u = (t - GULL_HOP.off) / 4; ctx.drawImage(GULL_SPR[step(t, 1440) % 3][1], Math.round(lerp(gx - 4, gx - 120, u)), Math.round(lerp(gy - 6, 120, Math.sqrt(u)))); }
  }
}

/* ================= Per-frame: strollers, the hot dog wagon and its customers, cyclists ================= */
const LANE_A_LIST = STROLL.filter(q => q[4] < 240).sort((a, b) => a[4] - b[4]), LANE_B_LIST = STROLL.filter(q => q[4] >= 240).sort((a, b) => a[4] - b[4]);
function drawStrollers(list, t, crowd) {
  for (const [st, k, ph, dir, fy, rank] of list) {
    if (rank >= crowd) continue;
    const d = frac(k * t / LOOP + ph) * SPAN, x = dir > 0 ? -22 + d : W + 22 - d;
    walker(st, x, fy, dir, d);
    if (st === 5) {                                                                  // the man in the hat walks his dog
      const dx = Math.round(x + dir * 13), fr = Math.floor(d / 4) & 1;
      ctx.drawImage(DOG_SPR[fr][dir > 0 ? 0 : 1], dx - 5, fy - 4);
      ctx.fillStyle = '#c8423a';
      for (let q = 1; q < 6; q++) ctx.fillRect(Math.round(x + dir * (3 + q * 1.4)), fy - 11 + q, 1, 1);
    }
  }
}
const DOG_STYLES = [3, 10, 0, 13, 7, 8];
function drawCart(t, crowd) {
  const tau = (t + LOOP - 2) % DOG_T, c = Math.floor(((t + LOOP - 2) % LOOP) / DOG_T), st = DOG_STYLES[c % 6];
  const serving = tau >= 26 && tau < 29, hx = CART.x + 22 + (serving ? 6 : Math.round(wave(t, 24, 0.2) * 3)), hy = CART.y + 13;
  halo(HL.cart, CART.x + 26, CART.y + 18, 0.9 + 0.06 * wave(t, 210));
  blit(CART_C);
  const none = nobody(crowd);                                                        // the wagon stands lit and steaming, unattended
  if (!none) {
    box(hx, hy, 4, 2, '#f4f0e8'); box(hx, hy + 2, 4, 3, '#e8b890'); box(hx - 1, hy + 5, 6, 6, '#f4f0e8'); box(hx, hy + 6, 4, 5, '#b8282e');   // the vendor: cap, apron
    if (serving) { box(hx + 5, hy + 6, 8, 1, '#f4f0e8'); box(hx + 12, hy + 5, 3, 1, '#e8b060'); box(hx + 12, hy + 4, 3, 1, '#c8423a'); }
  }
  for (let k = 0; k < 6; k++) {                                                      // steam from the stove pipe
    const { u } = cycle(t, 40 + k * 5, k * 0.19), rr = u < 0.4 ? 1 : 2;
    ctx.globalAlpha = 0.3 * Math.sin(Math.PI * u); ctx.fillStyle = '#dfe4f2';
    ctx.fillRect(Math.round(CART.x + 6 + u * 10 + wave(t, 17, k * 0.2) * 2 * u) - rr + 1, Math.round(CART.y - 12 - u * 24), rr * 2 - 1, rr * 2 - 1);
  }
  ctx.globalAlpha = 1;
  if (none) return;                                                                  // no customers, no tourist, no children running
  if (EATER_RANK[0] < crowd) stander(11, hash(step(t, 96), 4) < 0.5 ? 'eat' : 'idle', 82, 233, 1);
  if (EATER_RANK[1] < crowd) stander(4, hash(step(t + 1, 96), 6) < 0.4 ? 'eat' : 'idle', 94, 231, -1);
  // one customer after another: in from the right, a hot dog at the hatch, off to the left eating
  const s = along([[0, W + 24, 229], [24, 62, 232], [31, 62, 232], [40, -26, 229]], tau);
  if (s.moving) walker(st, s.x, s.y, -1, s.x); else stander(st, tau >= 28 ? 'eat' : 'idle', s.x, s.y, -1);
  // a tourist stops by the cycle lane for a picture; a cargo bike rings and he hops back
  if (t >= TOURIST.t0 && t < TOURIST.t1 + 12) {
    const q = along([[TOURIST.t0, W + 24, 232], [74, 300, 234], [86, 300, 234], [TOURIST.t1 + 12, -26, 230]], t);
    const hop = t >= TOURIST.bell - 0.6 && t < TOURIST.bell + 0.3 ? 3 : 0;
    if (q.moving) walker(2, q.x, q.y, -1, q.x); else stander(2, t < TOURIST.bell - 0.6 ? 'camera' : t < TOURIST.bell + 4 ? 'wave' : 'idle', q.x, q.y - hop, 1);
    if (!q.moving && ((t >= 76.5 && t < 76.7) || (t >= 77.6 && t < 77.8))) halo(HL.flash, 308, 214, 1);
  }
  // a child dashes over to jump at the soap bubbles
  if (t >= 120 && t < 138) {
    const q = along([[120, -20, 227], [129.5, BUBBLE_KID.x - 20, 221], [132, BUBBLE_KID.x - 20, 221], [138, W + 20, 226]], t);
    if (q.moving) walker(15, q.x, q.y, 1, q.x * 1.6); else stander(15, 'cheer', q.x, q.y - (step(t, 960) & 1 ? 3 : 0), 1);
  }
  // the child who runs at the gull
  if (t >= 49 && t < 82) { const q = along([[49, W + 20, 226], [55.8, BOLLARDS[1] + 12, 220], [62, 150, 225], [82, -20, 227]], t); walker(15, q.x, q.y, -1, q.x * 1.6); }
}
function drawBike(ri, x, wy, dir, d) {
  const spr = BIKE_SPR[ri][Math.floor(d / 5) & 3][dir > 0 ? 0 : 1], w = spr.width, X = Math.round(x) - (dir > 0 ? 12 : w - 12), Y = wy - CH + 1;
  ctx.drawImage(spr, X, Y);
  const lamp = RIDERS[ri].cargo ? 41 : 22;
  halo(HL.tiny, dir > 0 ? X + lamp : X + w - 1 - lamp, Y + 15, 0.9); halo(HL.tail, dir > 0 ? X + 1 : X + w - 2, Y + 21, 0.7);
  return X;
}
const BIKE_LIST = BIKES.slice().sort((a, b) => a[4] - b[4]);
function drawBikes(t, crowd) {
  if (t >= 68 && t < 94 && !nobody(crowd)) {                                         // the cargo bike with the bell
    const u = (t - 68) / 26, X = drawBike(4, lerp(W + 50, -60, u), 241, -1, u * 600);
    if (t >= TOURIST.bell - 1.4 && t < TOURIST.bell + 0.4) { const k = step(t, 1440) & 1; ctx.fillStyle = '#fff4d0'; ctx.fillRect(X + 22, 222 - k, 1, 1); ctx.fillRect(X + 20 - k, 220, 1, 1); ctx.fillRect(X + 24 + k, 220, 1, 1); ctx.fillRect(X + 22, 218 - k, 2, 1); }
  }
  for (const [ri, k, ph, dir, wy, rank] of BIKE_LIST) {
    if (rank >= crowd) continue;
    const d = frac(k * t / LOOP + ph) * BSPAN;
    drawBike(ri, dir > 0 ? -50 + d : W + 50 - d, wy, dir, d);
  }
}

/* ================= Per-frame: soap bubbles from the child's wand, carried west on the breeze (three depths) ================= */
function drawBubbles(layer, t) {
  const hx = BUBBLE_KID.x - 11, hy = BUBBLE_KID.y - 17;
  for (const b of BUBBLES[layer]) {
    const { cyc, u } = cycle(t, b.n, b.ph), h1 = hash(b.id, cyc, 1), h2 = hash(b.id, cyc, 2);
    const reach = [300 + h1 * 170, 150 + h1 * 190, 50 + h1 * 170][layer], rise = [70 + h2 * 110, 22 + h2 * 40, 30 + h2 * 64][layer];
    const x = hx - Math.pow(u, 0.85) * reach + wave(t, b.ks, b.p2) * (2 + layer), y = hy - Math.pow(u, 0.6) * rise + Math.sin(u * 9 + b.id) * 2;
    if (x < -8) continue;
    const ri = layer === 0 ? 0 : layer === 1 ? 1 + Math.floor(b.size * 2) : 2 + Math.floor(b.size * 2), r = ri + 1, X = Math.round(x), Y = Math.round(y);
    if (u > 0.94) {                                                                  // pop: four droplets fly apart
      const g = (u - 0.94) / 0.06;
      ctx.globalAlpha = (1 - g) * 0.9; ctx.fillStyle = '#e8f4ff';
      for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) ctx.fillRect(X + Math.round(dx * (r + g * 3)), Y + Math.round(dy * (r + g * 3)), 1, 1);
    } else {
      ctx.globalAlpha = Math.min(1, u * 14) * (layer === 0 ? 0.75 : 0.95);
      ctx.drawImage(BUB_SPR[ri], X - r, Y - r);
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the roof sign; billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the people
// to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all (see `nobody`)
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1), terr = terrLevel(crowd);
  const bubbles = wx.bubbles && !nobody(crowd);                                       // no child with a wand, no soap bubbles
  drawSky(t, crowd);
  for (let i = 0; i < 3; i++) drawGull(GULLS[i], t, i);
  drawRow(t, crowd);
  if (billboard) drawBoard(t, pitch);
  drawFarQuay(t, crowd, terr);
  drawShips(t, crowd);
  drawGull(GULLS[3], t, 3);
  if (bubbles) drawBubbles(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  drawCanal(t, MIRROR_LV[terr]);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);
  drawPicnic(t, crowd);
  drawTour(t, crowd);
  drawJazz(t, crowd);
  drawKayaks(t, crowd);
  drawSwans(t, crowd);
  if (bubbles) drawBubbles(1, t);
  drawEdge(t, crowd);
  drawStrollers(LANE_A_LIST, t, crowd);
  drawCart(t, crowd);
  drawBikes(t, crowd);
  drawStrollers(LANE_B_LIST, t, crowd);
  if (bubbles) drawBubbles(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'copenhagen',
  name: 'Copenhagen',
  tagline: 'Midsummer evening in Nyhavn',
  timeZone: 'Europe/Copenhagen',
  accent: '#ff8a00',                                   // Nyhavn orange, written out: tools/seo/seo.mjs reads it from the source
  focusX: 166,
  landmark: [HERO.x0, HOUSES[4].x0 + HOUSES[4].w],     // No. 9 (1681) and No. 11 with the schooner's masts before them
  landmarkTop: 22,                                     // the row of the schooner's main truck
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { crowd: 0.45, water: 0.3, birds: 0.1, wind: 0.06 },
  events: [
    { t: TOUR_OUT.t0 + 1, type: 'horn', x: 30, gain: 0.3 },
    { t: TOUR_OUT.t0 + 1.5, type: 'boat', dur: 44, pan: [-0.9, 0.9], gain: 0.35 },
    { t: 14, type: 'gull', x: 300, gain: 0.4 },
    { t: GULL_HOP.off, type: 'gull', x: BOLLARDS[1], gain: 0.55 },
    { t: TOURIST.bell - 1, type: 'chime', x: 320, gain: 0.45 },
    { t: FEED.splash, type: 'splash', x: 270, gain: 0.35 },
    { t: SAIL.up1, type: 'bell', x: 280, gain: 0.4 },
    { t: TOUR_IN.t0 + 1.5, type: 'boat', dur: 44, pan: [0.9, -0.9], gain: 0.35 },
    { t: 150, type: 'gull', x: 120, gain: 0.4 },
    { t: JAZZ.t0 + 2, type: 'boat', dur: 48, pan: [-0.9, 0.9], gain: 0.3 },
    { t: 206, type: 'chime', x: 140, gain: 0.3 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
